import React, { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect, useDeferredValue  } from 'react';
import api from './services/apiService';
import moment from 'moment';
import 'moment/locale/de'; // Importiere die deutsche Locale
import './styles.css'; // Verlinkung zur CSS-Datei
import { useNavigate } from 'react-router-dom'; // Importieren von useNavigate
import { Helmet } from 'react-helmet';
import { useVirtualizer } from '@tanstack/react-virtual';
import AsyncSelect from 'react-select/async';
import { createPortal } from 'react-dom';




// === SimpleModal (wie in PipelineInstanceForm) ===
function SimpleModal({ open, onClose, children }) {
  if (!open) return null;
  return (
    <div style={{
      position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
      background: "rgba(0,0,0,0.22)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999
    }}>
      <div
        className="simple-modal"
        style={{ background: "#fff", borderRadius: 10, minWidth: 350, padding: 28, boxShadow: "0 2px 16px rgba(0,0,0,0.13)", position: "relative" }}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 11,
            right: 16,
            fontSize: 32,
            color: "#e03131",
            border: "none",
            background: "none",
            cursor: "pointer",
            fontWeight: "bold",
            lineHeight: 1,
            padding: 0
          }}
          aria-label="Schließen"
        >×</button>
        {children}
      </div>
    </div>
  );
}

function CalendarTooltip({ tooltip }) {
  if (!tooltip) return null;

  return createPortal(
    <div
      className="calendar-tooltip-overlay"
      role="tooltip"
      style={{
        left: tooltip.left,
        top: tooltip.top,
        transform: tooltip.placeBelow
          ? 'translateX(-50%)'
          : 'translate(-50%, -100%)',
      }}
    >
      {tooltip.content}
    </div>,
    document.body
  );
}

// ⬇️ NEU: „weiß“ erkennen
function isWhite(hex) {
  return /^#?f{3,8}$/i.test((hex || '').replace('#', ''));
}

function getContrastColor(hexcolor) {
  if (!hexcolor) return "#111";
  hexcolor = hexcolor.replace('#','');
  if(hexcolor.length === 3){
    hexcolor = hexcolor.split('').map(x=>x+x).join('');
  }
  const r = parseInt(hexcolor.substr(0,2),16);
  const g = parseInt(hexcolor.substr(2,2),16);
  const b = parseInt(hexcolor.substr(4,2),16);
  const yiq = ((r*299)+(g*587)+(b*114))/1000;
  return (yiq >= 180) ? '#111' : '#fff';
}

// Normalisiert ISO („...Z“), ISO mit Offset ODER "YYYY-MM-DD" auf *lokalen* Kalendertag
const toLocalDay = (s) => {
  if (!s) return moment.invalid();
  const z = moment.parseZone(s);            // respektiert Offset/„Z“
  if (z.isValid()) return z.local().startOf('day');
  // Fallback für reine YYYY-MM-DD-Strings (z.B. aus rentalCalculator)
  return moment(s, 'YYYY-MM-DD', true).startOf('day');
};

const CALENDAR_AUTO_REFRESH_MS = 60 * 1000;
const CALENDAR_FOCUS_REFRESH_THROTTLE_MS = 5000;

const compactCustomerName = (customer) =>
  customer?.company?.trim()
  || [customer?.firstName, customer?.lastName].filter(Boolean).join(' ').trim()
  || `Kunde #${customer?.id}`;

const getCalendarCustomerMeta = (customer) => [
  customer?.companyAddon ? `Zusatz: ${customer.companyAddon}` : null,
  customer?.customerNumber ? `Kd.-Nr.: ${customer.customerNumber}` : null,
  customer?.debtorNumber ? `Deb.-Nr.: ${customer.debtorNumber}` : null,
  customer?.city || null,
].filter(Boolean);

const formatCalendarCustomerOptionLabel = (option, { context }) => {
  if (context === 'value' || !option.customer) return option.label;

  const meta = getCalendarCustomerMeta(option.customer);
  return (
    <div style={{ lineHeight: 1.25 }}>
      <div>{compactCustomerName(option.customer)}</div>
      {meta.length > 0 && (
        <div style={{ color: '#666', fontSize: 12, marginTop: 2 }}>
          {meta.join(' | ')}
        </div>
      )}
    </div>
  );
};

const formatHeight = (v) => {
  if (v == null || isNaN(v)) return '—';
  return `${new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v))} m`;
};

const formatWeight = (v) => {
  if (v == null || isNaN(v)) return '—';
  // Gewicht ohne Nachkommastellen, 3.500-Format
  return `${new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.round(Number(v)))} kg`;
};



// Setze die Locale auf Deutsch
moment.locale('de');

const formatMoney = (value) =>
  Number(value || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

const formatLadder = (ladder) => {
  if (!ladder) return 'keine Staffelinfo';
  const unit = ladder.unitName ? ` ${ladder.unitName}` : '';
  const source = ladder.source === 'customer' ? 'Kundenstaffel' : 'Typenstaffel';
  return `${source}: ${ladder.from}-${ladder.to}${unit}`;
};

function RentalPriceDecisionInfo({ info }) {
  if (!info) return null;

  return (
    <div style={{
      marginTop: 14,
      padding: 12,
      border: '1px solid #f0c36d',
      background: '#fff8e5',
      borderRadius: 6,
      display: 'grid',
      gap: 10
    }}>
      <div style={{ fontWeight: 'bold' }}>
        Durch die neue Mietdauer wechselt der Staffelpreis.
      </div>
      <div style={{ fontSize: 13 }}>
        Bisherige Mietdauer: {info.oldQuantity} Tag(e), neue Mietdauer: {info.newQuantity} Tag(e).
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div style={{ background: '#fff', border: '1px solid #ead8a0', borderRadius: 6, padding: 10 }}>
          <strong>Alten Einzelpreis beibehalten</strong>
          <div>{formatLadder(info.keep?.ladder)}</div>
          <div>Einzelpreis: {formatMoney(info.keep?.pricePerUnit)}</div>
          <div>Neuer Gesamtpreis: {formatMoney(info.keep?.total)}</div>
        </div>
        <div style={{ background: '#fff', border: '1px solid #b8ddb8', borderRadius: 6, padding: 10 }}>
          <strong>Staffelpreis aktualisieren</strong>
          <div>{formatLadder(info.update?.ladder)}</div>
          <div>Einzelpreis: {formatMoney(info.update?.pricePerUnit)}</div>
          <div>Neuer Gesamtpreis: {formatMoney(info.update?.total)}</div>
        </div>
      </div>
    </div>
  );
}


const suffixByContactFunction = (contactFunction) => {
  const cf = String(contactFunction || '').toLowerCase();
  if (cf === 'lw-bediener') return ' (Bed.)';
  if (cf === 'besteller')   return ' (Best.)';
  return '';
};




const RentalCalendar = () => {

const [showTodayOnLogin] = useState(() => sessionStorage.getItem('calendarShowToday') === 'true');

useEffect(() => {
  if (showTodayOnLogin) sessionStorage.removeItem('calendarShowToday');
}, [showTodayOnLogin]);

  // Refs/State für die Heute-Spaltenmarkierung
const wrapperRef = useRef(null);     // Wrapper um die Tabelle
const todayThRef = useRef(null);     // der <th> von "heute"
const rowsParentRef = useRef(null); 
const [todayOverlay, setTodayOverlay] = useState({ left: 0, width: 0, show: false });
const [rangeMode, setRangeMode] = useState(showTodayOnLogin ? 'custom' : 'month'); // 'month' | 'custom'
const [calendarScrollMaxHeight, setCalendarScrollMaxHeight] = useState(null);
const [calendarTooltip, setCalendarTooltip] = useState(null);

const showCalendarTooltip = useCallback((event, content) => {
  const rect = event.currentTarget.getBoundingClientRect();
  const halfTooltipWidth = 110;
  const viewportPadding = 12;
  const left = Math.min(
    Math.max(rect.left + rect.width / 2, halfTooltipWidth + viewportPadding),
    window.innerWidth - halfTooltipWidth - viewportPadding
  );
  const scrollBounds = rowsParentRef.current?.getBoundingClientRect();
  const spaceAbove = rect.top - (scrollBounds?.top ?? 0);
  const spaceBelow = (scrollBounds?.bottom ?? window.innerHeight) - rect.bottom;
  const placeBelow = spaceAbove < 170 && spaceBelow >= spaceAbove;

  setCalendarTooltip({
    content,
    left,
    top: placeBelow ? rect.bottom + 8 : rect.top - 8,
    placeBelow,
  });
}, []);

const hideCalendarTooltip = useCallback(() => {
  setCalendarTooltip(null);
}, []);

const openCalendarPdf = useCallback((documentPath) => {
  const baseUrl = String(api.defaults.baseURL || '').replace(/\/$/, '');
  window.open(
    `${baseUrl}/documents/${documentPath}`,
    '_blank',
    'noopener,noreferrer'
  );
}, []);

const openCalendarDeliveryNote = useCallback((orderId, orderItemId, variant) => {
  if (!orderId || !orderItemId) return;

  const endpoint = variant === 'ls2' ? 'delivery-note2' : 'delivery-note';
  const params = new URLSearchParams({ itemIds: String(orderItemId) });

  // Entspricht dem bestehenden LS1-Aufruf aus der Auftragsliste.
  if (variant === 'ls1') params.set('perDevice', 'true');

  openCalendarPdf(`${endpoint}/${orderId}/pdf?${params.toString()}`);
}, [openCalendarPdf]);

useEffect(() => {
  if (!calendarTooltip) return undefined;

  const hideOnViewportChange = () => setCalendarTooltip(null);
  window.addEventListener('scroll', hideOnViewportChange, true);
  window.addEventListener('resize', hideOnViewportChange);

  return () => {
    window.removeEventListener('scroll', hideOnViewportChange, true);
    window.removeEventListener('resize', hideOnViewportChange);
  };
}, [calendarTooltip]);


  const [rentalData, setRentalData] = useState([]);
  const [month, setMonth] = useState(
    !showTodayOnLogin && localStorage.getItem('month') ? parseInt(localStorage.getItem('month'), 10) : moment().month()
  );
  const [year, setYear] = useState(
    !showTodayOnLogin && localStorage.getItem('year') ? parseInt(localStorage.getItem('year'), 10) : moment().year()
  );
  const [startDate, setStartDate] = useState(moment().clone().subtract(showTodayOnLogin ? 21 : 20, 'days').startOf('day').toISOString()); // Nach Login wie „Heute“: ± 3 Wochen
  const [endDate, setEndDate] = useState(moment().clone().add(showTodayOnLogin ? 21 : 20, 'days').endOf('day').toISOString());
  const [offers, setOffers] = useState([]); // State für offers
  const [orders, setOrders] = useState([]); // Neuer State für Aufträge
  const navigate = useNavigate(); // Initialisieren von useNavigate
  const [isLoading, setIsLoading] = useState(true);
  const [lastCalendarRefreshAt, setLastCalendarRefreshAt] = useState(null);
  const [calendarAutoRefreshEnabled, setCalendarAutoRefreshEnabled] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [holidays, setHolidays] = useState([]);

  // ---- Schnellzugriffe (O(1)) als Maps/Sets ----
const ordersById = useMemo(() => {
  const m = new Map();
  for (const o of orders) m.set(o.id, o);
  return m;
}, [orders]);

const offersById = useMemo(() => {
  const m = new Map();
  for (const o of offers) m.set(o.id, o);
  return m;
}, [offers]);

const invoicesByOrderId = useMemo(() => {
  const m = new Map();
  for (const inv of invoices) {
    const arr = m.get(inv.orderId) || [];
    arr.push(inv);
    m.set(inv.orderId, arr);
  }
  return m;
}, [invoices]);

// Für den Order-Filter: lowercased Ordernummern
const orderNumberLookup = useMemo(() => {
  const m = new Map();
  for (const o of orders) m.set(o.id, (o.orderNumber || '').toLowerCase());
  return m;
}, [orders]);


  // --- NEU: Brand-Logo aus MessageTemplate 'brand_calendar' ---
const [brandSignature, setBrandSignature] = useState(null);

// Base64/URL normalisieren
const normalizeImageSrc = useCallback((val) => {
  if (!val) return null;
  if (val.startsWith('data:') || val.startsWith('http')) return val;
  // Annahme: nacktes Base64 → PNG
  return `data:image/png;base64,${val}`;
}, []);

useEffect(() => {
  (async () => {
    try {
      const res = await api.get('/messagetemplates/by-name/brand_calendar');
      const sig = res?.data?.signatureImage || null;
      console.log('[brand_calendar]', { status: res.status, hasSig: !!sig, len: sig?.length });
      setBrandSignature(sig);
    } catch (err) {
      console.warn('[brand_calendar] by-name failed → fallback list', err?.response?.status, err?.message);
      try {
        const list = await api.get('/messagetemplates');
        const tpl = (Array.isArray(list?.data) ? list.data : []).find(t => t?.name === 'brand_calendar');
        setBrandSignature(tpl?.signatureImage || null);
      } catch (e2) {
        console.error('[brand_calendar] kein Template ladbar:', e2);
      }
    }
  })();
}, []);



// === Portal-Mode & Modal-States =========================================
const [isPortal, setIsPortal] = useState(false);
const [portalCustomerId, setPortalCustomerId] = useState(null);
const [allowedProductGroupIds, setAllowedProductGroupIds] = useState([]);
const [allowedMachineTypeIds, setAllowedMachineTypeIds] = useState([]);
const [reloadTick, setReloadTick] = useState(0);
const [todayJumpTick, setTodayJumpTick] = useState(showTodayOnLogin ? 1 : 0);
const lastCenteredTodayJumpRef = useRef(0);
const lastCalendarAutoRefreshRef = useRef(0);

// Modal State
const [showPortalModal, setShowPortalModal] = useState(false);
const [modalMachine, setModalMachine] = useState(null);
const [modalDateStr, setModalDateStr] = useState(null);
const [modalDays, setModalDays] = useState(1);
const [modalIncludeSaturday, setModalIncludeSaturday] = useState(false);
const [modalIncludeSunday, setModalIncludeSunday] = useState(false);
const [modalIncludeHolidays, setModalIncludeHolidays] = useState(false);
const [modalOperationLocation, setModalOperationLocation] = useState('');
const [modalInternalNote, setModalInternalNote] = useState('');
const [isCreatingReservation, setIsCreatingReservation] = useState(false);
const [reservationSaved, setReservationSaved] = useState(false);
const [editNetTotal, setEditNetTotal] = useState(null);
const [isSavingEdit, setIsSavingEdit] = useState(false);
const [editSaved, setEditSaved] = useState(false);
const [showCreateChoiceModal, setShowCreateChoiceModal] = useState(false);
const [choiceMachine, setChoiceMachine] = useState(null);
const [choiceDateStr, setChoiceDateStr] = useState(null);
const [selectedCustomerOpt, setSelectedCustomerOpt] = useState(null); // { value, label }
const [customerPreselect, setCustomerPreselect] = useState(null);     // optionaler Vorbesetzer





// === Edit-Modal für Portal-Reservierungen ===
const [showEditReservationModal, setShowEditReservationModal] = useState(false);
const [editOffer, setEditOffer] = useState(null);          // komplettes Offer
const [editOfferItem, setEditOfferItem] = useState(null);  // das Miet-OfferItem
const [editOperationLocation, setEditOperationLocation] = useState('');
const [editInternalNote, setEditInternalNote] = useState('');

// Formularfelder im Edit-Modal
const [editDays, setEditDays] = useState(1);
const [editIncludeSaturday, setEditIncludeSaturday] = useState(false);
const [editIncludeSunday, setEditIncludeSunday] = useState(false);
const [editIncludeHolidays, setEditIncludeHolidays] = useState(false);

// Preis-Feedback im Modal nach dem Speichern
const [editJustSaved, setEditJustSaved] = useState(false);
const [editTotalNet, setEditTotalNet] = useState(null);
const [editUnitPrice, setEditUnitPrice] = useState(null);

const [createJustSaved, setCreateJustSaved] = useState(false);
const [createTotalNet, setCreateTotalNet] = useState(null);


// Swap-Modal
const [showSwapModal, setShowSwapModal] = useState(false);
const [swapOrder, setSwapOrder] = useState(null);        // kompletter Order (für ID + Edit-Link)
const [swapOrderItem, setSwapOrderItem] = useState(null);// angeklicktes Miet-OrderItem
const [swapStartDate, setSwapStartDate] = useState('');  // YYYY-MM-DD
const [swapNewItem, setSwapNewItem] = useState(null);    // {id,name}
const [isSwapping, setIsSwapping] = useState(false);

// Swap-Modal (Offer)
const [showSwapOfferModal, setShowSwapOfferModal] = useState(false);
const [swapOffer, setSwapOffer] = useState(null);              // komplettes Offer
const [swapOfferItem, setSwapOfferItem] = useState(null);      // angeklicktes Miet-OfferItem
const [swapOfferStartDate, setSwapOfferStartDate] = useState(''); // YYYY-MM-DD
const [swapOfferNewItem, setSwapOfferNewItem] = useState(null);   // {id,name}
const [isSwappingOffer, setIsSwappingOffer] = useState(false);


// Fulfill-Modal (Abmelden)
const [showFulfillModal, setShowFulfillModal] = useState(false);
const [fulfillOrder, setFulfillOrder] = useState(null);           // kompletter Order
const [fulfillOrderItem, setFulfillOrderItem] = useState(null);   // Miet-OrderItem
const [fulfillDate, setFulfillDate] = useState(moment().format('YYYY-MM-DDTHH:mm'));
const [fulfillContacts, setFulfillContacts] = useState([]);
const [fulfillContactId, setFulfillContactId] = useState('');
const [isFulfilling, setIsFulfilling] = useState(false);

// === Standtage (ALT+Klick) ===
const [showStandModal, setShowStandModal] = useState(false);
const [standOrder, setStandOrder] = useState(null);
const [standOrderItem, setStandOrderItem] = useState(null);
const [standFrom, setStandFrom] = useState('');
const [standTo, setStandTo] = useState('');
const [standItemId, setStandItemId] = useState(null);     // "Standtag"-Artikel via Name
const [standManualPrice, setStandManualPrice] = useState('');
const [isSplittingStand, setIsSplittingStand] = useState(false);
const [standPercent, setStandPercent] = useState('');        // z.B. "50"
const [standBaseUnitPrice, setStandBaseUnitPrice] = useState(null); // Tagespreis als Basis

// === Mietdauer-Anpassen (Kalender) ===
const [showRangeModal, setShowRangeModal] = useState(false);
const [rangeOrder, setRangeOrder] = useState(null);           // kompletter Order
const [rangeOrderItem, setRangeOrderItem] = useState(null);   // das Miet-OrderItem (Segment)
const [rangeFrom, setRangeFrom] = useState('');               // YYYY-MM-DD
const [rangeTo, setRangeTo] = useState('');                   // YYYY-MM-DD
const [isSavingRange, setIsSavingRange] = useState(false);
const [rangePriceDecision, setRangePriceDecision] = useState(null);
const [rangePendingPayload, setRangePendingPayload] = useState(null);

// ⬇️ direkt neben deinen anderen Modal-States
const [showActionModal, setShowActionModal] = useState(false);
const [actionOrder, setActionOrder] = useState(null);
const [actionOrderItem, setActionOrderItem] = useState(null);
const [actionMachine, setActionMachine] = useState(null);
const [actionDateStr, setActionDateStr] = useState(null);

// === Mietdauer-Anpassen für Offers (Kalender) ===
const [showOfferRangeModal, setShowOfferRangeModal] = useState(false);
const [rangeOffer, setRangeOffer] = useState(null);
const [rangeOfferItem, setRangeOfferItem] = useState(null);
const [rangeOfferFrom, setRangeOfferFrom] = useState('');
const [rangeOfferTo, setRangeOfferTo] = useState('');
const [isSavingOfferRange, setIsSavingOfferRange] = useState(false);
const [offerRangePriceDecision, setOfferRangePriceDecision] = useState(null);
const [offerRangePendingPayload, setOfferRangePendingPayload] = useState(null);

// === Haupt-Action-Modal (Angebot) ===
const [showOfferActionModal, setShowOfferActionModal] = useState(false);
const [actionOffer, setActionOffer] = useState(null);
const [actionOfferItem, setActionOfferItem] = useState(null);
const [actionOfferMachine, setActionOfferMachine] = useState(null);
const [actionOfferDateStr, setActionOfferDateStr] = useState(null);

const [showLocationModal, setShowLocationModal] = useState(false);
const [locationInfo, setLocationInfo] = useState(null);
// locationInfo: { machineName, lat, lng, timestamp }

const resetRangePricePrompt = () => {
  setRangePriceDecision(null);
  setRangePendingPayload(null);
};

const resetOfferRangePricePrompt = () => {
  setOfferRangePriceDecision(null);
  setOfferRangePendingPayload(null);
};

const closeRangeModal = () => {
  setShowRangeModal(false);
  setRangeOrder(null);
  setRangeOrderItem(null);
  setRangeFrom('');
  setRangeTo('');
  setIsSavingRange(false);
  resetRangePricePrompt();
};

const closeOfferRangeModal = () => {
  setShowOfferRangeModal(false);
  setRangeOffer(null);
  setRangeOfferItem(null);
  setRangeOfferFrom('');
  setRangeOfferTo('');
  setIsSavingOfferRange(false);
  resetOfferRangePricePrompt();
};

const saveOrderRentalRange = async (payload, updatePrice = true) => {
  if (!rangeOrder || !rangeOrderItem || !payload) return;
  try {
    setIsSavingRange(true);
    await api.patch(
      `/orders/${rangeOrder.id}/orderitems/${rangeOrderItem.id}/rental-range`,
      { ...payload, updatePrice }
    );
    closeRangeModal();
    setReloadTick(t => t + 1);
  } catch (e) {
    console.error('Mietdauer-Anpassung fehlgeschlagen:', e);
    const msg = e?.response?.data?.error || 'Ã„nderung konnte nicht gespeichert werden.';
    alert(msg);
  } finally {
    setIsSavingRange(false);
  }
};

const previewOrderRentalRange = async () => {
  if (!rangeOrder || !rangeOrderItem || !rangeFrom || !rangeTo) return;
  const payload = { rentalPeriodFrom: rangeFrom, rentalPeriodTo: rangeTo };
  try {
    setIsSavingRange(true);
    const response = await api.patch(
      `/orders/${rangeOrder.id}/orderitems/${rangeOrderItem.id}/rental-range`,
      { ...payload, preview: true }
    );

    if (response.data?.priceChangeRequired) {
      setRangePendingPayload(payload);
      setRangePriceDecision(response.data.priceInfo);
      return;
    }

    await saveOrderRentalRange(payload, true);
  } catch (e) {
    console.error('Mietdauer-Preview fehlgeschlagen:', e);
    const msg = e?.response?.data?.error || 'Ã„nderung konnte nicht geprÃ¼ft werden.';
    alert(msg);
  } finally {
    setIsSavingRange(false);
  }
};

const saveOfferRentalRange = async (payload, updatePrice = true) => {
  if (!rangeOffer || !rangeOfferItem || !payload) return;
  try {
    setIsSavingOfferRange(true);
    await api.patch(
      `/offers/${rangeOffer.id}/offeritems/${rangeOfferItem.id}/rental-range`,
      { ...payload, updatePrice }
    );
    closeOfferRangeModal();
    setReloadTick(t => t + 1);
  } catch (e) {
    console.error('Offer-Mietdauer-Anpassung fehlgeschlagen:', e);
    const msg = e?.response?.data?.error || 'Ã„nderung konnte nicht gespeichert werden.';
    alert(msg);
  } finally {
    setIsSavingOfferRange(false);
  }
};

const previewOfferRentalRange = async () => {
  if (!rangeOffer || !rangeOfferItem || !rangeOfferFrom || !rangeOfferTo) return;
  const payload = { rentalPeriodFrom: rangeOfferFrom, rentalPeriodTo: rangeOfferTo };
  try {
    setIsSavingOfferRange(true);
    const response = await api.patch(
      `/offers/${rangeOffer.id}/offeritems/${rangeOfferItem.id}/rental-range`,
      { ...payload, preview: true }
    );

    if (response.data?.priceChangeRequired) {
      setOfferRangePendingPayload(payload);
      setOfferRangePriceDecision(response.data.priceInfo);
      return;
    }

    await saveOfferRentalRange(payload, true);
  } catch (e) {
    console.error('Offer-Mietdauer-Preview fehlgeschlagen:', e);
    const msg = e?.response?.data?.error || 'Ã„nderung konnte nicht geprÃ¼ft werden.';
    alert(msg);
  } finally {
    setIsSavingOfferRange(false);
  }
};


/**
 * Prüft, ob ein Datum in der geladenen holidays-Liste ist.
 * @param {Date} date JS-Date-Objekt
 * @returns {boolean}
 */
const isHolidayDate = (date) => {
    const m = date.getMonth();     // 0 = Januar … 11 = Dezember
    const d = date.getDate();      // 1–31
    const y = date.getFullYear();
    return holidays.some(h =>
      Number(h.month) === m &&
      Number(h.day)   === d &&
      (h.isRecurring || Number(h.year) === y)
    );
  };


    useEffect(() => {
   if (showCreateChoiceModal) {
     setSelectedCustomerOpt(customerPreselect || null);
   }
 }, [showCreateChoiceModal]);

// Kunden-Loader für AsyncSelect
const loadCustomerOptions = async (inputValue) => {
  const q = (inputValue || '').trim();
  if (q.length < 2) return [];
  try {
    const { data } = await api.get('/customers/search', { params: { q, limit: 20 } });
    return (data || []).map(c => {
      const title = compactCustomerName(c);
      const meta = getCalendarCustomerMeta(c);
      return {
        value: c.id,
        label: meta.length ? `${title} (${meta.join(' | ')})` : title,
        customer: c,
      };
    });
  } catch (e) {
    console.error('loadCustomerOptions failed', e);
    return [];
  }
};

// === Range um +/- 14 Tage schieben (Top-Level im Component-Body!) ===
function shiftRange(deltaDays) {
  setRangeMode('custom');
  setStartDate(prev =>
    moment(prev).add(deltaDays, 'days').startOf('day').toISOString()
  );
  setEndDate(prev =>
    moment(prev).add(deltaDays, 'days').endOf('day').toISOString()
  );
}


const goToday = useCallback(() => {
  setRangeMode('custom');
  const today = moment();

  // 3 Wochen links / rechts
  const newStart = today.clone().subtract(21, 'days').startOf('day').toISOString();
  const newEnd   = today.clone().add(21, 'days').endOf('day').toISOString();

  setStartDate(newStart);
  setEndDate(newEnd);

  // optional aber sinnvoll: Dropdowns auf heutigen Monat/Jahr setzen
  setMonth(today.month());
  setYear(today.year());

  // triggert danach das Zentrieren per Effect
  setTodayJumpTick(t => t + 1);
}, []);

const refreshCalendarData = useCallback((force = false) => {
  const now = Date.now();
  if (!force && now - lastCalendarAutoRefreshRef.current < CALENDAR_FOCUS_REFRESH_THROTTLE_MS) {
    return;
  }

  lastCalendarAutoRefreshRef.current = now;
  setReloadTick(t => t + 1);
}, []);

useEffect(() => {
  let cancelled = false;

  api.get('/companysetups')
    .then(response => {
      const setup = Array.isArray(response.data) ? response.data[0] : response.data;
      if (!cancelled && setup) {
        setCalendarAutoRefreshEnabled(setup.calendarAutoRefreshEnabled !== false);
      }
    })
    .catch(error => {
      console.error('Fehler beim Laden der Kalender-Einstellung:', error);
    });

  return () => {
    cancelled = true;
  };
}, []);

useEffect(() => {
  if (!calendarAutoRefreshEnabled) return undefined;

  const refreshWhenVisible = () => {
    if (document.hidden) return;
    refreshCalendarData(false);
  };

  const handleVisibilityChange = () => {
    if (!document.hidden) refreshCalendarData(false);
  };

  window.addEventListener('focus', refreshWhenVisible);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  const intervalId = window.setInterval(refreshWhenVisible, CALENDAR_AUTO_REFRESH_MS);

  return () => {
    window.removeEventListener('focus', refreshWhenVisible);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.clearInterval(intervalId);
  };
}, [calendarAutoRefreshEnabled, refreshCalendarData]);


 // Feiertage einmal beim Mount laden
 useEffect(() => {
  api.get('/holidays')
     .then(res => {
       // res.data muss { month, day, year, isRecurring, … } liefern
       setHolidays(res.data);
     })
     .catch(console.error);
}, []);


function getBaseUnitPriceFromOrderItem(oi) {
  // ⬇️ Falls dein Tagespreis anders heißt, hier ergänzen/ändern:
  if (oi && Number.isFinite(Number(oi.pricePerUnit))) return Number(oi.pricePerUnit);
  if (oi && Number.isFinite(Number(oi.unitPrice)))     return Number(oi.unitPrice);
  if (oi && Number.isFinite(Number(oi.netPricePerDay)))return Number(oi.netPricePerDay);
  return null;
}

useEffect(() => {
  const p = String(standPercent).trim();
  if (p === '' || standBaseUnitPrice == null) return;

  const pct = Number(p.replace(',', '.'));
  if (!Number.isFinite(pct)) return;

  const value = Math.round((standBaseUnitPrice * pct / 100) * 100) / 100;
  setStandManualPrice(String(value.toFixed(2))); // schreibt direkt ins Preisfeld
}, [standPercent, standBaseUnitPrice]);


// Portal-Infos laden
useEffect(() => {
  (async () => {
    try {
      const me = await api.get('/auth/me');
      const customerIdFromMe = me?.data?.customerId ?? null;
      setIsPortal(!!customerIdFromMe);
      setPortalCustomerId(customerIdFromMe);
      const allowed = me?.data?.allowedProductGroupIds ?? [];
      setAllowedProductGroupIds(Array.isArray(allowed) ? allowed : []);
      const allowedMT = me?.data?.allowedMachineTypeIds ?? [];
      setAllowedMachineTypeIds(Array.isArray(allowedMT) ? allowedMT : []);
    } catch {
      setIsPortal(false);
    }
  })();
}, []);



  const createWorkshopDraftOrder = async (machineId, dateStr, inspectionSubtype) => {
    try {
      const dateStrPretty = moment(dateStr).format('DD.MM.YYYY');
      const payload = { 
        customerId: 1, 
        itemId: machineId, 
        date: dateStr,
        workshopOrderType: 'inspection', // oder ein anderer gültiger Wert
        notes: `${inspectionSubtype}-Prüfung am ${dateStrPretty}`
        
      };
      const response = await api.post('/workshoporders/draft', payload);
      // Nehmen wir an, response.data enthält das neu erstellte Objekt (mit .id)
      const newDraft = response.data;
      // Baue den Edit-URL selbst:
      navigate(`/workshoporders/${newDraft.id}/edit`);
    } catch (error) {
      console.error('Fehler beim Erstellen eines Werkstattauftrag-Entwurfs:', error);
      alert('Fehler beim Erstellen des Werkstattauftrag-Entwurfs!');
    }
  };

// Draft-Angebot erstellen (intern) und Maschine als Position hinzufügen
const createDraftOffer = async (machineId, machineName, dateStr, customerIdArg) => {
  try {
    // 1) Offer-Draft anlegen
    const draftRes = await api.post('/offers/draft', {
      customerId: customerIdArg,
      isReservation: true,
      status: 'draft'
    });
    const offerId = draftRes?.data?.offerId || draftRes?.data?.id;
    if (!offerId) throw new Error('OfferId fehlt.');

    // 2) Mietposition hinzufügen (1 Tag ab dateStr)
    await api.post(`/offers/${offerId}/offeritems`, {
      itemId: machineId,
      quantity: 1,
      unitId: 29,               // Tag
      rentalPeriodFrom: dateStr,
      includeSaturday: false,
      includeSunday: false,
      includeHolidays: false
    });

    // 3) Ins Offer-Edit navigieren
    navigate(`/offers/${offerId}/edit`);
  } catch (error) {
    console.error('Fehler beim Erstellen des Angebots inkl. Positionen:', error);
    alert('Angebot/Positionen konnten nicht angelegt werden.');
  }
};

  // Neue Funktion: Draft-Auftrag für Rental erstellen (wie in der OrderList)
// Neue Funktion: Draft-Auftrag für Rental erstellen + Maschine (inkl. Links) als Position(en) hinzufügen
// Draft-Auftrag für Rental erstellen + Maschine als Position hinzufügen (mit Kundenwahl)
const createDraftOrder = async (customerId, machineId, machineName, dateStr) => {
  try {
    if (!customerId) throw new Error('customerId fehlt');
    const payload = {
      customerId,
      date: dateStr,
    };
    const draftRes = await api.post('/orders/draft', payload);
    const orderId  = draftRes?.data?.orderId;
    const editUrl  = draftRes?.data?.editUrl;
    if (!orderId || !editUrl) throw new Error('Draft-Order ohne orderId/editUrl');

    await api.post(`/orders/${orderId}/orderitems`, {
      itemId: machineId,
      quantity: 1,
      unitId: 29, // Tag
      rentalPeriodFrom: dateStr,
      includeSaturday: false,
      includeSunday: false,
      includeHolidays: false
    });

    navigate(editUrl);
  } catch (error) {
    console.error('Fehler beim Erstellen des Auftrags inkl. Positionen:', error);
    alert('Auftrag/Positionen konnten nicht angelegt werden.');
  }
};


// NEU: Portal-Reservierung anlegen (Offer Draft + OfferItem)
const submitPortalReservation = useCallback(async () => {
  if (!isPortal || !portalCustomerId || !modalMachine || !modalDateStr) return;

  // 🚧 Doppel-Klicks abfangen
  if (isCreatingReservation || reservationSaved) return;

  try {
    setIsCreatingReservation(true);

    // 1) Draft anlegen
    const draftRes = await api.post('/offers/draft', {
      customerId: portalCustomerId,
      isReservation: true,
      status: 'draft',
      operationLocation: modalOperationLocation || null,
      internalNotes: modalInternalNote || '',
    });
    const offerId = draftRes?.data?.offerId;
    if (!offerId) throw new Error('OfferId fehlt.');

    // 2) OfferItem hinzufügen
const addItemRes = await api.post(`/offers/${offerId}/offeritems`, {
  itemId: modalMachine.id,
  quantity: Number(modalDays),
  unitId: 29, // Tag
  rentalPeriodFrom: modalDateStr,
  includeSaturday: modalIncludeSaturday,
  includeSunday: modalIncludeSunday,
  includeHolidays: modalIncludeHolidays
});

// Haupt-OfferItem ermitteln (je nach API-Response)
const mainItemId =
  addItemRes?.data?.mainOfferItem?.id ||
  addItemRes?.data?.id;

let recalcedTotal = addItemRes?.data?.mainOfferItem?.total ?? null;

// ⬇️ Touch-Update ausführen, um sofort die vollständige Recalc-Logik zu triggern
if (mainItemId) {
  const touchRes = await api.put(`/offers/${offerId}/offeritems/${mainItemId}`, {
    quantity: Number(modalDays),
    unitId: 29,
    rentalPeriodFrom: modalDateStr,
    includeSaturday: modalIncludeSaturday,
    includeSunday: modalIncludeSunday,
    includeHolidays: modalIncludeHolidays
  });
  if (typeof touchRes?.data?.total === 'number') {
    recalcedTotal = touchRes.data.total;
  }
}

// 3) Finalisieren
await api.put(`/offers/${offerId}`, { status: 'open' });

// 4) UI: „gespeichert“-Hinweis + Button sperren
setCreateTotalNet(recalcedTotal);
setCreateJustSaved(true);
setReservationSaved(true);           // ⚑ verhindert zweiten Submit
setReloadTick(t => t + 1);
} catch (err) {
  console.error('Portal-Reservierung fehlgeschlagen:', err);
  alert('Reservierung konnte nicht erstellt werden.');
} finally {
  setIsCreatingReservation(false);
}
}, [
  isPortal, portalCustomerId, modalMachine, modalDateStr, modalDays,
  modalIncludeSaturday, modalIncludeSunday, modalIncludeHolidays,
  modalOperationLocation, modalInternalNote,
  isCreatingReservation, reservationSaved
]);




// Eigene Reservierung zum Bearbeiten laden und Modal öffnen
const openEditReservationModal = async (offerId, machineId) => {
  try {
    const res = await api.get(`/offers/${offerId}`); // liefert offer inkl. offerItems
    const offer = res.data;

    // Das Miet-OfferItem für diese Maschine finden
    const oi = (offer.offerItems || []).find(
      oi => String(oi.itemId) === String(machineId)
    );
    if (!oi) throw new Error('OfferItem für Maschine nicht gefunden.');

    // Formular initialisieren
    setEditOffer(offer);
    setEditOfferItem(oi);
    setEditDays(Number(oi.quantity) || 1);
    setEditIncludeSaturday(!!oi.includeSaturday);
    setEditIncludeSunday(!!oi.includeSunday);
    setEditIncludeHolidays(!!oi.includeHolidays);
    setEditOperationLocation(offer.operationLocation || '');
    setEditInternalNote(offer.internalNotes || '');

    // Preis vorbelegen
    setEditNetTotal(typeof oi.total === 'number' ? oi.total : null);
    setEditTotalNet(typeof oi.total === 'number' ? oi.total : null);
    setEditSaved(false);

    setShowEditReservationModal(true);
  } catch (err) {
    console.error('Fehler beim Laden der Reservierung:', err);
    alert('Reservierung konnte nicht geladen werden.');
  }
};

const saveEditedReservation = async () => {
  try {
    if (!editOffer || !editOfferItem) return;
    if (isSavingEdit) return;              // ➕ Guard

    setIsSavingEdit(true);                 // ➕ Start Loading

    const respItem = await api.put(
      `/offers/${editOffer.id}/offeritems/${editOfferItem.id}`,
      {
        quantity: Number(editDays),
        unitId: editOfferItem.unitId || 29,
        includeSaturday: editIncludeSaturday,
        includeSunday: editIncludeSunday,
        includeHolidays: editIncludeHolidays,
        rentalPeriodFrom: editOfferItem.rentalPeriodFrom
      }
    );
    const updatedItem = respItem.data;

    // ➕ Preis aus Antwort übernehmen
    setEditNetTotal(typeof updatedItem?.total === 'number' ? updatedItem.total : null);
    setEditOfferItem(updatedItem);
    setEditUnitPrice(updatedItem?.pricePerUnit ?? null);
    setEditTotalNet(updatedItem?.total ?? null);

    await api.put(`/offers/${editOffer.id}`, {
      operationLocation: (editOperationLocation || '').trim() || null,
      internalNotes: (editInternalNote || '').trim()
    });

    setEditJustSaved(true);
    setTimeout(() => setEditJustSaved(false), 3500);

    setReloadTick(t => t + 1);
  } catch (err) {
    console.error('Speichern der Reservierung fehlgeschlagen:', err);
    alert('Änderungen konnten nicht gespeichert werden.');
  } finally {
    setIsSavingEdit(false);               // ➕ Ende Loading
  }
};





// Handler für freie Tage: Nur für "rental" Artikel
const handleFreeDayClick = (machine, dateStr) => {
  const productGroup = productGroups.find(
    (pg) => String(pg.id) === String(machine.productGroupId)
  );
  if (!productGroup) {
    console.warn("Keine Produktgruppe für Maschine gefunden:", machine);
    return;
  }
  // Konvertiere den Namen in Kleinbuchstaben und trimme Leerzeichen
  const groupName = productGroup.name.trim().toLowerCase();
  console.log("Produktgruppe (rental):", groupName);
  if (groupName.includes('rental')) {
    // Für Mietartikel: POST-Request an /orders/draft
    createDraftOrder(machine.id, dateStr);
  } else {
    console.log("handleFreeDayClick: Kein Rental-Fall, daher keine Aktion.");
  }
};



   // Neue States für Filter
   const [selectedProductGroup, setSelectedProductGroup] = useState(
    localStorage.getItem('selectedProductGroup') || 'all'
  );
  const [selectedStorageLocation, setSelectedStorageLocation] = useState(
    localStorage.getItem('selectedStorageLocation') || 'all'
  );
  const [selectedMotorization, setSelectedMotorization] = useState(
    localStorage.getItem('selectedMotorization') || 'all'
  );

  
  // NEU: Mindest-Arbeitshöhe (als String, '' = kein Filter)
  const [minHeightFilter, setMinHeightFilter] = useState(() => {
    const saved = localStorage.getItem('minHeightFilter');
    return saved ?? '';
  });


   // Neue Filter-States: Maschine, Kundenname, Auftragsnummer – initial aus localStorage laden
   const [machineFilter, setMachineFilter] = useState(localStorage.getItem('machineFilter') || '');
   const [customerFilter, setCustomerFilter] = useState(localStorage.getItem('customerFilter') || '');
   const [orderFilter, setOrderFilter] = useState(localStorage.getItem('orderFilter') || '');

  
   const deferredMachineFilter = useDeferredValue(machineFilter);
   const deferredCustomerFilter = useDeferredValue(customerFilter);
   const deferredOrderFilter = useDeferredValue(orderFilter);

   


  // Speichere die Filter in localStorage, wenn sich die Werte ändern:
  useEffect(() => {
    localStorage.setItem('selectedProductGroup', selectedProductGroup);
    localStorage.setItem('selectedStorageLocation', selectedStorageLocation);
    localStorage.setItem('selectedMotorization', selectedMotorization);
  }, [selectedProductGroup, selectedStorageLocation, selectedMotorization]);

  // Speichere Monat und Jahr, wenn sie sich ändern:
useEffect(() => {
  localStorage.setItem('month', month);
  localStorage.setItem('year', year);
}, [month, year]);

useEffect(() => {
  localStorage.setItem('machineFilter', machineFilter);
}, [machineFilter]);

useEffect(() => {
  localStorage.setItem('customerFilter', customerFilter);
}, [customerFilter]);

useEffect(() => {
  localStorage.setItem('orderFilter', orderFilter);
}, [orderFilter]);

// NEU: Mindest-Arbeitshöhe speichern
useEffect(() => {
  localStorage.setItem('minHeightFilter', minHeightFilter);
}, [minHeightFilter]);


    // ---- NEU: States zum Speichern der geladenen Filterdaten ----
    const [productGroups, setProductGroups] = useState([]);
    const [storageLocations, setStorageLocations] = useState([]);
    const [motorTypes, setMotorTypes] = useState([]);


   // Laden der Filterdaten (Produktgruppen und Motorisierungen)
  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const pgResponse = await api.get('/product-groups/used');
        console.log('ProductGroups:', pgResponse.data);

        const allGroups = pgResponse.data;
setProductGroups(
  isPortal && allowedProductGroupIds.length > 0
    ? allGroups.filter(g => allowedProductGroupIds.map(String).includes(String(g.id)))
    : allGroups
);
        const mtResponse = await api.get('/motortypes/used');
        console.log('MotorTypes:', mtResponse.data);
        setMotorTypes(mtResponse.data);

        const storageLocationResponse = await api.get('/storage-locations');
        setStorageLocations(storageLocationResponse.data || []);
      } catch (error) {
        console.error('Fehler beim Laden der Filterdaten:', error);
      }
    };
    fetchFilterData();
  }, []);


  useEffect(() => {
  api.get('/invoices')
    .then(response => {
      setInvoices(response.data);
      // HIER:
      console.log("Geladene Invoices im Kalender:", response.data);
    })
    .catch(error => console.error('Fehler beim Laden der Rechnungen:', error));
}, [reloadTick]);

  /**
   * Aktualisiert das Start- und Enddatum basierend auf der ausgewählten Monat und Jahr.
   * Der zentrale Punkt ist der 15. Tag des ausgewählten Monats.
   * Der angezeigte Zeitraum ist 20 Tage vor und 20 Tage nach diesem zentralen Datum.
   */
  useEffect(() => {
  if (rangeMode !== 'month') return;

  const centerDate = moment({ year: year, month: month, day: 15 });
  const newStartDate = centerDate.clone().subtract(20, 'days').startOf('day').toISOString();
  const newEndDate   = centerDate.clone().add(20, 'days').endOf('day').toISOString();
  setStartDate(newStartDate);
  setEndDate(newEndDate);
}, [month, year, rangeMode]);

 

  // Lade die Verfügbarkeitsdaten für den ausgewählten Monat und Jahr
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true); // Ladeanzeige starten
      try {
        // ─── NEU: Feiertage laden ───────────────────────────────────
// Parallel laden
const [
  holidaysResponse,
  allItemsResponse,
  offersResponse,
  ordersResponse,
  transportMarkersResponse,
  serviceMarkersResponse,
  workshopResponse,
] = await Promise.all([
  api.get('/holidays'),
  api.get('/items'),
  api.get('/offers', { params: { startDate, endDate, reloadTick } }),
  api.get('/orders', { params: { startDate, endDate, reloadTick } }),
  api.get('/transportmarkers'),
  api.get('/servicemarkers'),
  api.get('/workshoporders', { params: { startDate, endDate, reloadTick } }),
]);

const loadedHolidays = holidaysResponse.data;
setHolidays(loadedHolidays);

const allMachines = allItemsResponse.data;
// ⬇️ Standtag-Artikel-ID ermitteln (CompanySetup bevorzugt; Fallback: Items-Suche)
try {
  if (!standItemId) {
    let detected = null;

    // 1) Bevorzugt: aus CompanySetup
    try {
      const cs = await api.get('/companysetup'); // ggf. Endpoint anpassen
      detected = cs?.data?.standItemId ?? null;
    } catch {}

    // 2) Fallback: in Items suchen
    if (!detected) {
      const exact = (allMachines || []).find(it =>
        String(it?.name || '').trim().toLowerCase() === 'standtag'
      );
      const fuzzy = exact || (allMachines || []).find(it => {
        const n = String(it?.name || '').toLowerCase();
        return n.includes('standtag') || n.includes('standtage') || n.includes('standzeit');
      });
      detected = exact?.id || fuzzy?.id || null;
    }

    if (detected) {
      setStandItemId(detected);
    } else {
      console.warn('[standtag] Kein Standtag-Artikel gefunden. CompanySetup.standItemId setzen oder Item „Standtag“ anlegen.');
    }
  }
} catch (e) {
  console.warn('[standtag] Ermittlung fehlgeschlagen:', e);
}


// ✅ Nur Offers mit Angebotsnummer anzeigen (Drafts haben keine)
let offersData = (offersResponse.data || [])
  .filter(o => o.status !== 'ordered')
  .filter(o => String(o?.offerNumber || '').trim() !== '');

setOffers(offersData);

// ✅ Nur Orders mit Auftragsnummer anzeigen (Drafts haben keine)
const ordersData = (ordersResponse.data || []).filter(o => String(o?.orderNumber || '').trim() !== '');
setOrders(ordersData);

const transportMarkers = transportMarkersResponse.data;
const serviceMarkers = serviceMarkersResponse.data;
const workshopOrders = workshopResponse.data;




        // Filtere zunächst alle Items, die standardmäßig im Kalender sichtbar sein sollen
        let machines = allMachines.filter(item => item.visibleInCalendar === true);

        if (isPortal && allowedProductGroupIds.length > 0) {
  machines = machines.filter(m => allowedProductGroupIds.map(String).includes(String(m.productGroupId)));
}
if (isPortal && allowedMachineTypeIds.length > 0) {
  machines = machines.filter(m => allowedMachineTypeIds.map(String).includes(String(m.machineTypeId)));
}


        // Falls ein Workshopauftrag ein Gerät referenziert, das noch nicht in "machines" enthalten ist, füge es hinzu:
        workshopOrders.forEach(workshop => {
  // Vergleiche als Strings, um Typkonflikte zu vermeiden
  const found = machines.find(m => String(m.id) === String(workshop.itemId));
  if (!found) {
    const machineToAdd = allMachines.find(
      m => String(m.id) === String(workshop.itemId)
    );
    // Nur hinzufügen, wenn sichtbar sein darf:
    if (machineToAdd && machineToAdd.visibleInCalendar === true) {
      machines.push(machineToAdd);
    }
  }
});





        // Mapping der Maschinen
        const mappedMachines = machines.map((machine) => ({
          id: machine.id,
          name: machine.name,
          color: machine.color || null,
          technicalDataOwner: machine.technicalDataOwner || null,   // ⬅️ hinzu
          availability: {}, // Verfügbarkeit nach Datum
          transportAvailability: {}, // TransportMarker-Verfügbarkeit
          serviceAvailability: {},
          standAvailability: {}, 
          productGroupId: machine.productGroupId,
          storageLocationId: machine.storageLocationId,
          storageLocation: machine.storageLocation || null,
          motorTypeId: machine.motorTypeId,
          itemType: machine.itemType,
          maxWorkingHeight: machine.maxWorkingHeight ?? null,
          totalWeight: machine.totalWeight ?? null,
          basketArm: machine.basketArm ?? false,
          foreignArticle: machine.foreignArticle ?? false,
        }));

        // Maschinen alphabetisch nach Namen sortieren
        mappedMachines.sort((a, b) => a.name.localeCompare(b.name));

        // ────────────────────────────────────────────────────────────────────────────
// 3A) NEU: „inspectionAvailability“ für jede Maschine initialisieren
mappedMachines.forEach((mappedMachine) => {
  // Finde zum „mappedMachine.id“ den Original-Datensatz in „machines“
  // (… machines ist das Array, das du oben aus /items geladen hast …)
  const original = machines.find(m => String(m.id) === String(mappedMachine.id));
  if (!original) return;

  // Erzeuge ein leeres Objekt, in dem später pro Tag (YYYY-MM-DD) ein Array aus Prüfungs­einträgen steht
  mappedMachine.inspectionAvailability = {};

  // Hol alle fünf Prüfungsfelder aus „original“
  // original.uvvInspection, original.tuvInspection, original.tachoInspection,
  // original.asuInspection, original.otherInspection
  // Sie können entweder null sein oder einen ISO-String wie "2025-05-31T00:00:00.000Z"

  const pruefungsFelder = [
    { key: 'uvvInspection',   label: 'UVV' },
    { key: 'tuvInspection',   label: 'TÜV' },
    { key: 'tachoInspection', label: 'Tacho' },
    { key: 'asuInspection',   label: 'ASU' },
    { key: 'otherInspection', label: 'Sonst.' }
  ];

  pruefungsFelder.forEach(({ key, label }) => {
    const isoValue = original[key]; // z. B. "2025-05-31T00:00:00.000Z" oder null
    if (!isoValue) return;         // überspringen, wenn kein Datum vorhanden

    // Kürze ISO-String auf reines "YYYY-MM-DD"
    const dateOnly = isoValue.split('T')[0]; // z. B. "2025-05-31"

    // Lege für dieses Datum (dateOnly) das Array in inspectionAvailability an, falls noch nicht da
    if (!mappedMachine.inspectionAvailability[dateOnly]) {
      mappedMachine.inspectionAvailability[dateOnly] = [];
    }

    // Schiebe einen Eintrag ins Array:
    // Wir merken uns hier { type: 'inspection', subtype: 'UVV' } o. Ä., damit wir später
    // wissen, welcher Prüfungs-Typ (UVV/TÜV/Tacho/etc.) angezeigt werden soll.
    mappedMachine.inspectionAvailability[dateOnly].push({
      type: 'inspection',
      subtype: label,
      // Optional: ID oder sonstige Infos, falls du später auf Klick ein anderes Verhalten willst
      // z. B. inspectionId: original.id  oder  inspectionType: key
    });
  });
});
// ────────────────────────────────────────────────────────────────────────────

        setRentalData(mappedMachines);

       // --- Neuer Block: Workshopaufträge den Maschinen zuordnen ---
workshopOrders.forEach((workshop) => {
  const { blockedPeriodFrom, blockedPeriodTo, itemId, id } = workshop;
  if (!blockedPeriodFrom || !blockedPeriodTo) return;
  // Finde die Maschine anhand der itemId des Workshopauftrags (IDs als String vergleichen)
  const machine = mappedMachines.find(m => String(m.id) === String(itemId));
  if (!machine) return;
  
  // Initialisiere das Feld workshopAvailability, falls noch nicht vorhanden
  if (!machine.workshopAvailability) {
    machine.workshopAvailability = {};
  }
  
  let start = moment(blockedPeriodFrom);
  let end = moment(blockedPeriodTo);
  
  // Füge für jeden Tag im gesperrten Zeitraum einen Eintrag hinzu
  while (start.isSameOrBefore(end)) {
    const dateStr = start.format('YYYY-MM-DD');
    if (!machine.workshopAvailability[dateStr]) {
      machine.workshopAvailability[dateStr] = [];
    }
    machine.workshopAvailability[dateStr].push({
      type: 'workshop',
      workshopId: id,
      workshopOrderType: workshop.workshopOrderType,
      workshopOrderNumber: workshop.workshopOrderNumber,
      notes: workshop.notes
    });
    start.add(1, 'day');
  }
});
//    console.log("Mapped Machines with Workshop Data:", mappedMachines);


       // Verarbeitung der Angebote
offersData.forEach((offer) => {
  const { isReservation, offerItems, customer, id } = offer;

  // Nur Reservierungen berücksichtigen
  if (!isReservation) return;

  // Kundenkürzel
  const customerCode = customer?.company
    ? customer.company.slice(0, 3).toUpperCase()
    : 'N/A';

  offerItems
    .filter(item => item.item.itemType === 'rental') // Nur Mietartikel
    .forEach(item => {
      const machine = mappedMachines.find(m => m.id === item.item.id);
      if (!machine) return;

      // Hol Flags direkt vom Item
      const includeSaturday = item.includeSaturday;
      const includeSunday   = item.includeSunday;
      const includeHolidays = item.includeHolidays;

      // Validität prüfen
      if (!item.rentalPeriodFrom || !item.rentalPeriodTo) {
        console.warn(`OfferItem ${item.id} hat keine gültigen Mietperioden.`);
        return;
      }

      const from = toLocalDay(item.rentalPeriodFrom);
const end  = toLocalDay(item.rentalPeriodTo);

let currentDate = from.clone();
while (currentDate.isSameOrBefore(end, 'day')) {
        const dateStr   = currentDate.format('YYYY-MM-DD');
        const dayOfWeek = currentDate.day();             // 0=So, 6=Sa
        const holiday = loadedHolidays.some(h =>
   Number(h.month) === currentDate.month()   &&
   Number(h.day)   === currentDate.date()    &&
   (h.isRecurring || Number(h.year) === currentDate.year())
 );

        // Überspringe, falls Feiertag/Sa/So nicht erlaubt
        if ((holiday   && !includeHolidays) ||
            (dayOfWeek === 6 && !includeSaturday)  ||
            (dayOfWeek === 0 && !includeSunday)
        ) {
          currentDate.add(1, 'day');
          continue;
        }

        // Anlage ins Array
        if (!machine.availability[dateStr]) {
          machine.availability[dateStr] = [];
        }
        machine.availability[dateStr].push({
          customerCode,
          offerId: offer.id,
          type: 'offer',
        });

    //    console.log(`Reserviere ${dateStr} für Maschine ${machine.name} (Angebot)`);

        currentDate.add(1, 'day');
      }
    });
});


        // Verarbeitung der Aufträge
ordersData.forEach(order => {
  const { orderItems, customer, id: orderId } = order;
  console.log(`Verarbeite Order ${orderId}:`, order);

  if (!orderItems || orderItems.length === 0) {
    console.warn(`Order ${orderId} hat keine orderItems.`);
    return;
  }

  // Kundenkürzel
  const customerCode = customer?.company
    ? customer.company.slice(0, 3).toUpperCase()
    : 'N/A';

  orderItems
    .filter(item => item.item.itemType === 'rental') // Nur Mietartikel
    .forEach(item => {
      const machine = mappedMachines.find(m => m.id === item.item.id);
      if (!machine) return;

      // Flags direkt vom Item
      const includeSaturday = item.includeSaturday;
      const includeSunday   = item.includeSunday;
      const includeHolidays = item.includeHolidays;

      // Validität prüfen
      if (!item.rentalPeriodFrom || !item.rentalPeriodTo) {
        console.warn(`OrderItem ${item.id} hat keine gültigen Mietperioden.`);
        return;
      }

      const from = toLocalDay(item.rentalPeriodFrom);
const end  = toLocalDay(item.rentalPeriodTo);

let currentDate = from.clone();
while (currentDate.isSameOrBefore(end, 'day')) {
        const dateStr   = currentDate.format('YYYY-MM-DD');
        const dayOfWeek = currentDate.day();   // 0=Sonntag, 6=Samstag
        const holiday = loadedHolidays.some(h =>
   Number(h.month) === currentDate.month()   &&
   Number(h.day)   === currentDate.date()    &&
   (h.isRecurring || Number(h.year) === currentDate.year())
 );

        // Überspringe, falls Feiertag/Sa/So nicht erlaubt
        if ((holiday   && !includeHolidays) ||
            (dayOfWeek === 6 && !includeSaturday)  ||
            (dayOfWeek === 0 && !includeSunday)
        ) {
          currentDate.add(1, 'day');
          continue;
        }

        // Eintrag ins Array
        if (!machine.availability[dateStr]) {
          machine.availability[dateStr] = [];
        }
        machine.availability[dateStr].push({
          customerCode,
          offerId: order.id,
          orderItemId: item.id,  // ⬅️ eindeutig das Segment referenzieren
          type: 'order',
        });

     //   console.log(`Reserviere ${dateStr} für Maschine ${machine.name} (Auftrag)`);

        currentDate.add(1, 'day');
      }
    });
});

// ───────────────────────────────────────────────────────────────
// STANDTAGE: primär über mainItemId, andernfalls eindeutiger Overlap-Fallback
ordersData.forEach(order => {
  const orderItems = order.orderItems || [];
  if (!orderItems.length) return;

  // 1) Miet-Segmente indexieren
  const rentals = orderItems
    .filter(oi => oi.item?.itemType === 'rental' && (oi.item?.id || oi.itemId))
    .map(oi => ({
      id: String(oi.id),
      machineId: (oi.item?.id ?? oi.itemId),
      from: oi.rentalPeriodFrom ? moment(oi.rentalPeriodFrom) : null,
      to:   oi.rentalPeriodTo   ? moment(oi.rentalPeriodTo)   : null,
    }));
  const rentalById = Object.fromEntries(rentals.map(r => [r.id, r]));

    // 2) Standtag-Positionen (robust: per ID ODER Name)
  const standItems = orderItems.filter(oi => {
    const nameIsStand = String(oi.item?.name || '').trim().toLowerCase() === 'standtag';
    const idIsStand   = standItemId && (String(oi.itemId) === String(standItemId));
    return (nameIsStand || idIsStand) && oi.rentalPeriodFrom && oi.rentalPeriodTo;
  });

  // Overlap-Helper
  const overlaps = (aFrom, aTo, bFrom, bTo) =>
    aFrom && aTo && bFrom && bTo && aFrom.isSameOrBefore(bTo, 'day') && bFrom.isSameOrBefore(aTo, 'day');

  // 3) Standtage auf genau eine Maschine mappen
  standItems.forEach(stand => {
    const standFrom = moment(stand.rentalPeriodFrom);
    const standTo   = moment(stand.rentalPeriodTo);

    // a) Primär: via mainItemId
    let parent = stand.mainItemId ? rentalById[String(stand.mainItemId)] : null;

    // b) Fallback: genau ein Mietsegment mit Datums-Overlap
    if (!parent) {
      const candidates = rentals.filter(r => overlaps(standFrom, standTo, r.from, r.to));
      parent = (candidates.length === 1) ? candidates[0] : null;
    }

    if (!parent) {
      // kein eindeutiger Bezug → bewusst nichts markieren
      return;
    }

    const machine = mappedMachines.find(m => String(m.id) === String(parent.machineId));
    if (!machine) return;

    let cur = standFrom.clone();
    while (cur.isSameOrBefore(standTo, 'day')) {
      const ymd = cur.format('YYYY-MM-DD');
      if (!machine.standAvailability[ymd]) machine.standAvailability[ymd] = [];
      machine.standAvailability[ymd].push({
        type: 'stand',
        orderId: order.id,
        orderNumber: order.orderNumber,
        standOrderItemId: stand.id
      });
      cur.add(1, 'day');
    }
  });
});






// ───────────────────────────────────────────────────────────────


            // Verarbeitung der TransportMarker
transportMarkers.forEach((marker) => {
  const {  delivery, pickup, orderItem } = marker;

  // Finde die Maschine anhand der orderItem.itemId
  const machine = mappedMachines.find((m) => m.id === orderItem.itemId);

  if (machine) {
    // Initialisiere transportAvailability, falls nicht vorhanden
    if (!machine.transportAvailability) {
      machine.transportAvailability = {};
    }

    // Füge den Delivery-Marker hinzu
    const deliveryDate = moment(delivery).format('YYYY-MM-DD');
    if (!machine.transportAvailability[deliveryDate]) {
      machine.transportAvailability[deliveryDate] = [];
    }
    machine.transportAvailability[deliveryDate].push({
      type: 'delivery',
      orderId: orderItem.orderId,
      orderNumber: orderItem.order.orderNumber, // Für Tooltip
      transportMarkerId: marker.id,
    });

    // Füge den Pickup-Marker hinzu
    const pickupDate = moment(pickup).format('YYYY-MM-DD');
    if (!machine.transportAvailability[pickupDate]) {
      machine.transportAvailability[pickupDate] = [];
    }
    machine.transportAvailability[pickupDate].push({
      type: 'pickup',
      orderId: orderItem.orderId,
      orderNumber: orderItem.order.orderNumber, // Für Tooltip
      transportMarkerId: marker.id,
    });
  }
});

 // Verarbeitung der ServiceMarker (Integration)
 serviceMarkers.forEach(marker => {
  const { UG, RN, orderItem } = marker;
  // Wir gehen davon aus, dass orderItem.itemId bereits als Zahl vorliegt.
  const machine = mappedMachines.find(m => m.id === orderItem.itemId);
  if (machine) {
    if (!machine.serviceAvailability) machine.serviceAvailability = {};
    // Falls UG (Service Start) vorhanden:
    if (UG) {
      const UGDate = moment(UG).format('YYYY-MM-DD');
      if (!machine.serviceAvailability[UGDate]) {
        machine.serviceAvailability[UGDate] = [];
      }
      machine.serviceAvailability[UGDate].push({
        type: 'UG',
        orderId: orderItem.orderId,
        orderNumber: orderItem.order.orderNumber,
        serviceMarkerId: marker.id,
        UGTime: marker.UGTime,
    RNTime: marker.RNTime, // falls du beide Zeiten brauchst
    
    serviceNotes: marker.serviceNotes,
      });
    } else {
      console.warn('ServiceMarker ohne UG:', marker);
    }
    // Falls RN (Service Ende) vorhanden:
    if (RN) {
      const RNDate = moment(RN).format('YYYY-MM-DD');
      if (!machine.serviceAvailability[RNDate]) {
        machine.serviceAvailability[RNDate] = [];
      }
      machine.serviceAvailability[RNDate].push({
        type: 'RN',
        orderId: orderItem.orderId,
        orderNumber: orderItem.order.orderNumber,
        serviceMarkerId: marker.id,
        UGTime: marker.UGTime,
    RNTime: marker.RNTime,
    operationLocationUG: marker.operationLocationUG,
    operationLocationRN: marker.operationLocationRN,
    serviceNotes: marker.serviceNotes,
      });
    } else {
      console.warn('ServiceMarker ohne RN:', marker);
    }
  } else {
    console.warn('Keine Maschine gefunden für ServiceMarker:', marker);
  }
});


        setRentalData(mappedMachines); // Aktualisieren der Mietdaten
        setLastCalendarRefreshAt(new Date());
      } catch (error) {
        console.error('Fehler beim Abrufen der Daten:', error);
      } finally {
        setIsLoading(false); // Ladeanzeige beenden
      }
    };

    

    fetchData();
  }, [startDate, endDate, reloadTick]);



  // Heute-Spalte relativ zum Wrapper messen (kein offsetLeft!)
useLayoutEffect(() => {
  const th = todayThRef.current;
  const wrap = wrapperRef.current;
  if (!th || !wrap) {
    setTodayOverlay({ left: 0, width: 0, show: false });
    return;
  }

  const thRect = th.getBoundingClientRect();
  const wrapRect = wrap.getBoundingClientRect();

  const left  = thRect.left - wrapRect.left;
  const width = thRect.width;

  setTodayOverlay({
    left: Math.max(left - 1, 0),  // 1px Puffer gegen Border-Kollision
    width: width + 2,             // links+rechts 1px
    show: true
  });
}, [
  // neu vermessen, wenn sich die Range oder Daten ändern
  startDate, endDate, rentalData, offers, orders
]);

// Beim horizontalen Scroll erneut (leichtgewichtig) vermessen
useEffect(() => {
  const wrap = wrapperRef.current;
  if (!wrap) return;

  let raf = 0;

  const remeasure = () => {
    const th = todayThRef.current;
    const wrapEl = wrapperRef.current;
    if (!th || !wrapEl) {
      setTodayOverlay({ left: 0, width: 0, show: false });
      return;
    }
    const thRect = th.getBoundingClientRect();
    const wrapRect = wrapEl.getBoundingClientRect();
    const left  = thRect.left - wrapRect.left;
    const width = thRect.width;
    setTodayOverlay({
      left: Math.max(left - 1, 0),
      width: width + 2,
      show: true
    });
  };

  const onScroll = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(remeasure);
  };

  wrap.addEventListener('scroll', onScroll, { passive: true });
  return () => {
    wrap.removeEventListener('scroll', onScroll);
    cancelAnimationFrame(raf);
  };
}, [startDate, endDate]);


useLayoutEffect(() => {
  // Erst nach dem Laden zentrieren, einmal pro Login oder Klick auf „Heute“.
  if (isLoading || !todayJumpTick || lastCenteredTodayJumpRef.current === todayJumpTick) return;

  const scroller = rowsParentRef.current;
  const th = todayThRef.current;
  if (!scroller || !th) return;

  let raf1 = 0, raf2 = 0;

  raf1 = requestAnimationFrame(() => {
    raf2 = requestAnimationFrame(() => {
      const scrollerRect = scroller.getBoundingClientRect();
      const thRect = th.getBoundingClientRect();

      const thLeftInScroller = (thRect.left - scrollerRect.left) + scroller.scrollLeft;
      const targetLeft = thLeftInScroller - (scroller.clientWidth / 2) + (thRect.width / 2);

      scroller.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
      lastCenteredTodayJumpRef.current = todayJumpTick;
    });
  });

  return () => {
    cancelAnimationFrame(raf1);
    cancelAnimationFrame(raf2);
  };
}, [todayJumpTick, startDate, endDate, isLoading]);



  // Funktion zur Generierung der Jahr-Optionen (Zukunftsjahre zuerst, dann vergangene Jahre)
 const generateYearOptions = () => {
  const currentYear = moment().year();     // z. B. 2025
  const futureYearsCount = 2;               // Anzahl der zukünftigen Jahre
  const pastYearsCount   = 5;               // Anzahl der vergangenen Jahre (inkl. aktuellem)

  // Berechne das größte und das kleinste in der angezeigten Spanne:
  const startYear = currentYear + futureYearsCount;               // z. B. 2025 + 2 = 2027
  const endYear   = currentYear - (pastYearsCount - 1);           // 2025 - (5-1) = 2021

  const years = [];
  for (let y = startYear; y >= endYear; y--) {
    years.push(y);
  }
  return years;
};

  /**
   * Handler für die Änderung des ausgewählten Monats.
   * Aktualisiert den State und damit den angezeigten Zeitraum.
   */
const handleMonthChange = (event) => {
  setRangeMode('month');
  setMonth(parseInt(event.target.value, 10));
};

  /**
   * Handler für die Änderung des ausgewählten Jahres.
   * Aktualisiert den State und damit den angezeigten Zeitraum.
   */
const handleYearChange = (event) => {
  setRangeMode('month');
  setYear(parseInt(event.target.value, 10));
};

   // ---- Handler für die Filter-Dropdowns ----
   const handleProductGroupChange = (event) => {
    setSelectedProductGroup(event.target.value);
  };

  const handleStorageLocationChange = (event) => {
    setSelectedStorageLocation(event.target.value);
  };

  const handleMotorizationChange = (event) => {
    setSelectedMotorization(event.target.value);
  };

  // Handler für die neuen Filter-Eingaben
  const handleMachineFilterChange = (event) => {
    setMachineFilter(event.target.value);
  };

 

  const handleOrderFilterChange = (event) => {
    setOrderFilter(event.target.value);
  };
  

  // Vorschlagslisten (diese Listen erzeugen eindeutige Werte aus deinen Daten)
  const uniqueMachineNames = useMemo(() => {
    return Array.from(new Set(rentalData.map(item => item.name)));
  }, [rentalData]);

  const customerOptions = useMemo(() => {
    const codes = new Set();
    rentalData.forEach(item => {
      Object.values(item.availability || {}).forEach(events => {
        events.forEach(ev => {
          if (ev.customerCode) codes.add(ev.customerCode);
        });
      });
      if (item.workshopAvailability) {
        Object.values(item.workshopAvailability).forEach(events => {
          events.forEach(ev => {
            if (ev.customerCode) codes.add(ev.customerCode);
          });
        });
      }
    });
    return Array.from(codes);
  }, [rentalData]);

  const orderOptions = useMemo(() => {
  return orders
    .map(o => o.orderNumber)
    .filter(Boolean);
}, [orders]);

// NEU: Statistiken für Arbeitshöhen (Slider-Bereich)
const heightStats = useMemo(() => {
  const values = rentalData
    .map(m => m.maxWorkingHeight)
    .filter(v => v != null && !isNaN(v));

  if (!values.length) {
    return { min: 0, max: 0 };
  }

  const min = Math.floor(Math.min(...values));
  const max = Math.ceil(Math.max(...values));
  return { min, max };
}, [rentalData]);


  // ---- Gefilterte Daten (Items) basierend auf den Filter-States ----
 // Gefilterte Daten (Items) basierend auf allen Filter-States
const filteredRentalData = useMemo(() => {
  const needleMachine  = deferredMachineFilter.trim().toLowerCase();
  const needleCustomer = deferredCustomerFilter.trim().toLowerCase();
  const needleOrder    = deferredOrderFilter.trim().toLowerCase();
  const minHeight      = minHeightFilter === '' ? null : Number(minHeightFilter);

  return rentalData.filter(item => {
    // Produktgruppe & Motorisierung
    const matchesGroup =
      selectedProductGroup === 'all' ||
      String(item.productGroupId) === String(selectedProductGroup);

    const matchesStorageLocation =
      selectedStorageLocation === 'all' ||
      String(item.storageLocationId) === String(selectedStorageLocation);

    const matchesMotor =
      selectedMotorization === 'all' ||
      String(item.motorTypeId) === String(selectedMotorization);

    // Maschinenname
    const matchesMachine =
      needleMachine === '' ||
      item.name.toLowerCase().includes(needleMachine);

    // Events der Maschine einsammeln (Offer/Order + Workshop)
    const events = [];
    Object.values(item.availability || {}).forEach(arr => { events.push(...arr); });
    if (item.workshopAvailability) {
      Object.values(item.workshopAvailability).forEach(arr => { events.push(...arr); });
    }

    // Kundenkürzel-Filter
    let matchesCustomer = (needleCustomer === '');
    if (!matchesCustomer) {
      matchesCustomer = events.some(ev =>
        ev.customerCode && ev.customerCode.toLowerCase().includes(needleCustomer)
      );
    }

    // Auftragsnummer-Filter (nur type==='order'), nutzt Map orderNumberLookup
    let matchesOrder = (needleOrder === '');
    if (!matchesOrder) {
      matchesOrder = events.some(ev => {
        if (ev.type !== 'order') return false;
        // Hinweis: ev.offerId entspricht hier der orderId
        const onumLC = orderNumberLookup.get(ev.offerId) || '';
        return onumLC.includes(needleOrder);
      });
    }

    // NEU: Arbeitshöhen-Filter (minHeight = Mindest-Arbeitshöhe)
    const matchesHeight =
      minHeight == null ||
      (
        item.maxWorkingHeight != null &&
        !isNaN(item.maxWorkingHeight) &&
        Number(item.maxWorkingHeight) >= minHeight
      );

    return matchesGroup && matchesStorageLocation && matchesMotor && matchesMachine && matchesCustomer && matchesOrder && matchesHeight;
  });
}, [
  rentalData,
  selectedProductGroup,
  selectedStorageLocation,
  selectedMotorization,
  deferredMachineFilter,
  deferredCustomerFilter,
  deferredOrderFilter,
  orderNumberLookup, // Map: orderId -> lowercased orderNumber
  minHeightFilter 
]);

// Ab wann lohnt sich Virtualisierung / Scroll?
const VIRT_THRESHOLD = 100; // <- hier deine Schwelle
const useVirtual = filteredRentalData.length > VIRT_THRESHOLD;

useLayoutEffect(() => {
  const updateCalendarScrollHeight = () => {
    const scroller = rowsParentRef.current;
    if (!scroller) return;

    const rect = scroller.getBoundingClientRect();
    const viewportPadding = 8;
    const nextHeight = Math.max(
      260,
      Math.floor(window.innerHeight - rect.top - viewportPadding)
    );

    setCalendarScrollMaxHeight((currentHeight) =>
      Math.abs((currentHeight || 0) - nextHeight) > 1 ? nextHeight : currentHeight
    );
  };

  updateCalendarScrollHeight();
  window.addEventListener('resize', updateCalendarScrollHeight);
  window.addEventListener('scroll', updateCalendarScrollHeight, { passive: true });

  return () => {
    window.removeEventListener('resize', updateCalendarScrollHeight);
    window.removeEventListener('scroll', updateCalendarScrollHeight);
  };
}, [filteredRentalData.length, startDate, endDate, isLoading]);

// Zeilen-Virtualisierung: erzeugt nur sichtbare Geräte-Zeilen
const rowVirtualizer = useVirtualizer({
  count: filteredRentalData.length,
  getScrollElement: () => rowsParentRef.current,
  estimateSize: () => 50, // entspricht der festen Kalender-Zeilenhoehe im CSS
  overscan: 20,
});
  /**
   * Generiert eine Liste von Moment-Objekten für jeden Tag im definierten Zeitraum.
   * Wird memoisiert, um unnötige Berechnungen bei Re-Renders zu vermeiden.
   */
  const generateDateRange = useMemo(() => {
    const dates = [];
    let currentDate = moment(startDate);
    const end = moment(endDate);
    while (currentDate.isBefore(end) || currentDate.isSame(end, 'day')) {
      dates.push(currentDate.clone());
      currentDate.add(1, 'day');  // Nächster Tag
    }
    return dates;
  }, [startDate, endDate, reloadTick]);


async function openFulfillModalFor(order, machineRowItem, clickedDateStr) {
  try {
    // Miet-OrderItem zu dieser Maschinenzeile finden
    const oi = order.orderItems?.find(oi =>
      String(oi.item?.id) === String(machineRowItem.id) &&
      oi.item?.itemType === 'rental'
    );
    if (!oi) {
      alert('Kein Miet-OrderItem für diese Maschine im Auftrag gefunden.');
      return;
    }

    // Kontakte des Kunden laden
    const customerId = order.customer?.id || order.customerId;
    let contacts = [];
    try {
      const res = await api.get(`/orders/contacts/customer/${customerId}`);
      contacts = Array.isArray(res.data) ? res.data : [];
    } catch (e) {
      console.warn('Kontakte konnten nicht geladen werden:', e);
    }

    setFulfillOrder(order);
    setFulfillOrderItem(oi);
    setFulfillContacts(contacts);
    setFulfillContactId('');
    // Default-Datum: angeklickter Tag oder heute
    const now = moment(); // aktuelle lokale Zeit
setFulfillDate(
  clickedDateStr
    ? `${clickedDateStr}T${now.format('HH:mm')}`
    : now.format('YYYY-MM-DDTHH:mm')
);

    setShowFulfillModal(true);
  } catch (e) {
    console.error('openFulfillModalFor error:', e);
  }
}



// Holt die aktuelle Position der Maschine und öffnet Google Maps
const openMachineLocationInMaps = async (machine) => {
  try {
    const res = await api.get(`/telematics/item/${machine.id}/position`);
    const payload = res.data;

    if (!payload || payload.ok === false) {
      const msg =
        payload?.message ||
        'Standort konnte nicht abgerufen werden (Telematik-Antwort fehlerhaft).';
      alert(msg);
      return;
    }

    const pos = payload.data?.position;
    if (!pos || pos.lat == null || pos.lng == null) {
      alert('Für diese Maschine ist aktuell kein Telematik-Standort verfügbar.');
      return;
    }

    const { lat, lng, timestamp } = pos;

    // 👉 Daten ins State, Modal öffnen
    setLocationInfo({
      machineName: machine.name,
      lat,
      lng,
      timestamp: timestamp || null,
    });
    setShowLocationModal(true);
  } catch (err) {
    console.error('Fehler beim Laden der Maschinenposition:', err);

    const msgFromBackend = err?.response?.data?.message || err.message;
    if (msgFromBackend) {
      alert(msgFromBackend); // z.B. "Tecuso: Keine Units in der Antwort gefunden"
    } else {
      alert('Standort konnte nicht abgerufen werden.');
    }
  }
};

const handleConfirmOpenMaps = () => {
  if (!locationInfo) return;

  const { lat, lng } = locationInfo;

  const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    lat
  )},${encodeURIComponent(lng)}`;

  window.open(url, '_blank', 'noopener,noreferrer');

  setShowLocationModal(false);
  setLocationInfo(null);
};


  /**
   * Generiert die Zeilen des Kalenders für jede Maschine.
   * Jede Zelle repräsentiert einen Tag und zeigt an, ob die Maschine reserviert ist.
   * Reservierte Zellen sind klickbar und navigieren zur Angebotsdetailseite oder Auftragsdetailseite.
   */

  

   // Generiere die Zeilen der Maschinen (optional mit Teilmenge)
 const generateItemRows = (items) => {
   const source = items || filteredRentalData;
   return source.map((item) => {
      const { availability, transportAvailability, serviceAvailability } = item;
      const productGroup = productGroups.find(pg => String(pg.id) === String(item.productGroupId));
      const groupColor = productGroup?.color || '#fff';
      const itemColor  = (item.color || '').trim();

      // ⬇️ NEU: Item-Farbe nur nutzen, wenn nicht „weiß“
const ownColor = (item.color && !isWhite(item.color)) ? item.color : null;
const bgColor  = ownColor || groupColor;

// optional: Hex-Check, damit getContrastColor nicht ausrastet
const isHex = (c) => /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(c);

// Regel: nutze itemColor, wenn vorhanden und ≠ Gruppenfarbe (und gültig)
const baseColor =
  (itemColor && itemColor !== groupColor && isHex(itemColor))
    ? itemColor
    : groupColor;
  
      return (
        <tr key={item.id}>
       {/* Maschinenname klickbar machen */}
<td
  onClick={() => {
    if (isPortal) {
      const raw = (item.technicalDataOwner || '').trim();
      if (!raw) return; // kein Link hinterlegt → nichts tun

      const href = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
      window.open(href, '_blank');
      return;
    }

    const frontendURL = window.location.origin;
    window.open(`${frontendURL}/itemform/${item.id}`, '_blank');
  }}
  style={{
    cursor: isPortal ? (item.technicalDataOwner ? 'pointer' : 'default') : 'pointer',
    background: bgColor,                    // ⬅️ geändert
    color: getContrastColor(bgColor),       // ⬅️ bleibt korrekt
    position: 'relative',
  }}
  title={isPortal && !item.technicalDataOwner ? 'Kein TD-Link hinterlegt' : undefined}
>
   {/* 📍 Standort-Icon – nur intern anzeigen, nicht im Portal */}
  {!isPortal && (
    <button
      type="button"
      className="location-button"
      title="Standort in Google Maps öffnen"
      onClick={(e) => {
        e.stopPropagation();             // verhindert den normalen Zellen-Klick
        openMachineLocationInMaps(item); // ⬅️ nutzt den Helper aus Schritt 1
      }}
    >
      📍
    </button>
  )}
  <div
  className="item-name-line"
  style={{ fontStyle: item.foreignArticle ? 'italic' : 'normal' }}
>
  {item.name}
</div>
  <div className="item-meta-line">
    {Number.isFinite(Number(item?.maxWorkingHeight))
      ? `${new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(item.maxWorkingHeight))} m`
      : '—'}
    {', '}
    {Number.isFinite(Number(item?.totalWeight))
      ? `${new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.round(Number(item.totalWeight)))} kg`
      : '—'}
    {item?.basketArm ? ', Korbarm' : ''}
  </div>
</td>



  
          {/* Für jeden Tag im angezeigten Bereich */}
          {generateDateRange.map((date) => {
  const dateStr = date.format('YYYY-MM-DD');
  const dateStrPretty = date.format('DD.MM.YYYY'); 

  // NEU: hat irgendein OrderItem dieser Maschine heute ein fulfillmentDate?
const hasCompletion = orders.some(o =>
  (o.orderItems || []).some(oi => {
    const sameItem =
      String(oi.itemId) === String(item.id) ||
      String(oi.item?.id) === String(item.id);
    return (
      sameItem &&
      oi.fulfillmentDate &&
      moment(oi.fulfillmentDate).isSame(dateStr, 'day')
    );
  })
);

  // Hole Reservierungen und Workshopdaten
  const dailyReservations = availability[dateStr] || [];
  const dailyWorkshop = (item.workshopAvailability && item.workshopAvailability[dateStr]) || [];
  const dailyStand        = (item.standAvailability && item.standAvailability[dateStr]) || [];
  
  // Kombiniere beide Arrays
  const dailyEvents = [...dailyReservations, ...dailyWorkshop];

  // Feiertag & Wochenende prüfen
  const dayOfWeek = date.day(); // 0=Sonntag, 6=Samstag
  const isWeekend = [0, 6].includes(dayOfWeek);
  const holiday = isHolidayDate(date.toDate());

  // Falls keine Events vorliegen, lege den Hintergrund fest
  let cellClass = '';
  if (dailyEvents.length === 0) {
    cellClass = holiday ? 'holiday-day' : isWeekend ? 'weekend-day' : 'free-day';
  }

  const isToday = date.isSame(moment(), 'day');
  if (isToday) {
    cellClass = `${cellClass} today-col`.trim();
  }
  // ⬇️ Portal: „Grün“ = free-day; alles andere als „nicht grün“ flaggen
const isGreenCell = cellClass.includes('free-day');
const disabledForPortal = isPortal && !isGreenCell;

  
            // Transportmarker (Lieferung/Abholung) für diesen Tag
            const transportMarkersForDay = transportAvailability?.[dateStr] || [];
  
            return (
              <td
  key={dateStr}
  className={`calendar-day-cell ${cellClass} ${disabledForPortal ? 'portal-disabled' : ''}`}
  style={{
    position: 'relative',
    verticalAlign: 'top'
  }}
  // Klicks auf komplette Zelle in „nicht grün“ für Portal sicher unterbinden
  onClick={disabledForPortal ? (e) => e.stopPropagation() : undefined}
>
                {/* 
                  Container, der mehrere Reservierungen nebeneinander anzeigen kann.
                  (Verwendet display:flex in der CSS.)
                */}
                <div className="reservation-split-container">
   {(dailyReservations.length > 0 || dailyWorkshop.length > 0 || dailyStand.length > 0) ? (
   // 1) STAND zuerst rendern (als Hinterlegung)
   [...dailyStand].map((event, index) => (
     <div
       key={`stand-${index}`}
       className="reservation-block stand-day-block"
       title={isPortal ? undefined : `Standtag\nAuftrag: ${event.orderNumber || event.orderId}`}
       // kein onClick – reines Markup
     >
       {/* schmaler Labeltext, optional */}
       <span className="stand-label">STAND</span>
     </div>
   ))
 ).concat(
   // 2) Danach deine normalen Events (Offer/Order/Workshop)
   [...dailyReservations, ...dailyWorkshop].map((event, index) => {
// ===== START PATCH: offer/order Block =====
if (event.type === 'offer' || event.type === 'order') {
  const isOffer = event.type === 'offer';
  const id = event.offerId;
  const offer = isOffer ? offersById.get(id) : null;
  const order = !isOffer ? ordersById.get(id) : null;

  // Kundenkürzel
  const customerCode = event.customerCode || '???';

  // Farbe je Typ
  const blockColorClass = isOffer ? 'occupied-offer-day' : 'occupied-order-day';

  // ---------- Portal-Owner-Erkennung (ROBUST) ----------
  // Auftrags-Kunden-ID sicher ermitteln (order.customer.id ODER order.customerId)
  const orderCustomerId =
    (order?.customer && order.customer.id != null) ? order.customer.id :
    (order?.customerId != null ? order.customerId : null);

  // Eigener Auftrag? (Portal sieht blau, NICHT klickbar)
  const isOwnOrder =
    isPortal &&
    !isOffer &&
    orderCustomerId != null &&
    String(orderCustomerId) === String(portalCustomerId);

  // Eigene Reservierung? (Portal darf weiter bearbeiten; klickbar)
  const isOwnReservation =
    isPortal &&
    isOffer &&
    !!offer?.isReservation &&
    offer?.customerId != null &&
    String(offer.customerId) === String(portalCustomerId);

  // ---------- Bestehende Billed/Completed-Logik ----------
  let billedClass = "";
  let completedClass = "";
  if (!isOffer && order) {
    const invoicesForOrder = invoicesByOrderId.get(order.id) || [];
    const hasUncreditedFinal = invoicesForOrder.some(inv =>
      inv?.invoiceType &&
      String(inv.invoiceType).toLowerCase() === "finalinvoice" &&
      !inv?.creditNoteCreated
    );

    const findOrderItemForThisCell = () => {
      if (!order?.orderItems?.length) return null;
      const d = toLocalDay(dateStr);
      return order.orderItems.find(oi => {
        const sameItem =
          String(oi.itemId) === String(item.id) ||
          String(oi.item?.id) === String(item.id);
        if (!sameItem || !oi.rentalPeriodFrom || !oi.rentalPeriodTo) return false;
        const from = toLocalDay(oi.rentalPeriodFrom);
        const to   = toLocalDay(oi.rentalPeriodTo);
        return d.isSameOrAfter(from, 'day') && d.isSameOrBefore(to, 'day');
      }) || null;
    };

    const oiForCell = findOrderItemForThisCell();

    const billedByItem =
      oiForCell?.billedUntil &&
      moment(dateStr).isSameOrBefore(moment(oiForCell.billedUntil), "day");

    if (hasUncreditedFinal || billedByItem) {
      billedClass = " billed-day";
    }

    if (oiForCell?.fulfillmentDate) {
      const fulfilledYMD = moment(oiForCell.fulfillmentDate).format("YYYY-MM-DD");
      if (fulfilledYMD === dateStr) {
        completedClass = " completed-day";
      }
    }
  }

  // Klassen zusammensetzen (+ Owner-Flags)
  const finalClass =
    blockColorClass +
    billedClass +
    completedClass +
    (isOwnOrder ? ' own-order' : '') +
    (isOwnReservation ? ' own-reservation' : '');

  // ---------- Portal: Inline-Style, der ALLES überstimmt ----------
  // Eigene Aufträge: Blau (standard), NICHT klickbar
  // Eigene Reservierungen: farbig & klickbar
  const portalVisualStyle = isPortal
    ? (isOwnOrder
        ? {
            backgroundColor: '#228be6',   // Standard-Blau
            borderColor: '#1c7ed6',
            color: '#fff',
            filter: 'none',
            opacity: 1,
            cursor: 'default',
            pointerEvents: 'none',        // NICHT klickbar
          }
        : isOwnReservation
          ? {
              filter: 'none',
              opacity: 1,
              cursor: 'pointer',          // klickbar (öffnet Edit-Modal)
            }
          : undefined)
    : undefined;

  // Klickverhalten
  const handleClick = (e) => {
    // Portal: nur eigene Reservierungen editieren, sonst nichts
    if (isPortal) {
      if (isOffer && offer?.isReservation && String(offer?.customerId) === String(portalCustomerId)) {
        openEditReservationModal(offer.id, item.id);
      }
      return;
    }

  // Ctrl/Cmd + Klick: Zeitraum anpassen (jetzt auch für OFFERS)
if (e.ctrlKey || e.metaKey) {
  const d = toLocalDay(dateStr);

  if (order) {
    // (vorhanden) – Order-Segment für den Tag finden und Order-Range-Modal öffnen
    const oiForCell = order.orderItems?.find(oi => {
      const sameItem =
        String(oi.itemId) === String(item.id) ||
        String(oi.item?.id) === String(item.id);
      if (!sameItem || oi.item?.itemType !== 'rental') return false;
      if (!oi.rentalPeriodFrom || !oi.rentalPeriodTo) return false;
      const from = toLocalDay(oi.rentalPeriodFrom);
      const to   = toLocalDay(oi.rentalPeriodTo);
      return d.isSameOrAfter(from, 'day') && d.isSameOrBefore(to, 'day');
    });

    if (!oiForCell) {
      alert('Kein passendes Miet-Segment für diesen Tag gefunden.');
      return;
    }

    setRangeOrder(order);
    setRangeOrderItem(oiForCell);
    setRangeFrom(moment(oiForCell.rentalPeriodFrom).format('YYYY-MM-DD'));
    setRangeTo(moment(oiForCell.rentalPeriodTo).format('YYYY-MM-DD'));
    setShowRangeModal(true);
    return;
  }

  if (offer) {
    // ⬅️ NEU: Offer-Segment finden und Offer-Range-Modal öffnen
    const oiForCell = offer.offerItems?.find(oi => {
      const sameItem = String(oi.item?.id) === String(item.id);
      if (!sameItem || oi.item?.itemType !== 'rental') return false;
      if (!oi.rentalPeriodFrom || !oi.rentalPeriodTo) return false;
      const from = moment(oi.rentalPeriodFrom);
      const to   = moment(oi.rentalPeriodTo);
      return d.isSameOrAfter(from, 'day') && d.isSameOrBefore(to, 'day');
    });

    if (!oiForCell) {
      alert('Kein passendes Miet-Segment für diesen Tag gefunden.');
      return;
    }

    setRangeOffer(offer);
    setRangeOfferItem(oiForCell);
    setRangeOfferFrom(moment(oiForCell.rentalPeriodFrom).format('YYYY-MM-DD'));
    setRangeOfferTo(moment(oiForCell.rentalPeriodTo).format('YYYY-MM-DD'));
    setShowOfferRangeModal(true);
    return;
  }
}


  // ⬇️ NEU: Ctrl/Cmd + Klick ⇒ Zeitraum (From/To) im Kalender anpassen
  if ((e.ctrlKey || e.metaKey) && order) {
    // Das Miet-Segment, das genau diesen Kalendertag abdeckt, ermitteln:
    const d = toLocalDay(dateStr);
    const oiForCell = order.orderItems?.find(oi => {
      const sameItem =
        String(oi.itemId) === String(item.id) ||
        String(oi.item?.id) === String(item.id);
      if (!sameItem || oi.item?.itemType !== 'rental') return false;
      if (!oi.rentalPeriodFrom || !oi.rentalPeriodTo) return false;
      const from = toLocalDay(oi.rentalPeriodFrom);
      const to   = toLocalDay(oi.rentalPeriodTo);
      return d.isSameOrAfter(from, 'day') && d.isSameOrBefore(to, 'day');
    });

    if (!oiForCell) {
      alert('Kein passendes Miet-Segment für diesen Tag gefunden.');
      return;
    }

    setRangeOrder(order);
    setRangeOrderItem(oiForCell);
    setRangeFrom(moment(oiForCell.rentalPeriodFrom).format('YYYY-MM-DD'));
    setRangeTo(moment(oiForCell.rentalPeriodTo).format('YYYY-MM-DD'));
    setShowRangeModal(true);
    return;
  }

   // ALT+Klick -> Standtage markieren (Segment GENAU für den geklickten Tag wählen)
if (e.altKey && order) {
  const d = toLocalDay(dateStr);
  const oiForCell = order.orderItems?.find(oi => {
    const sameItem =
      String(oi.itemId) === String(item.id) ||
      String(oi.item?.id) === String(item.id);
    if (!sameItem || oi.item?.itemType !== 'rental') return false;
    if (!oi.rentalPeriodFrom || !oi.rentalPeriodTo) return false;
    const from = toLocalDay(oi.rentalPeriodFrom);
    const to   = toLocalDay(oi.rentalPeriodTo);
    return d.isSameOrAfter(from, 'day') && d.isSameOrBefore(to, 'day');
  });

  if (!oiForCell) {
    alert('Kein passendes Miet-Segment für diesen Tag gefunden.');
    return;
  }

  setStandOrder(order);
  setStandOrderItem(oiForCell);   // ← korrektes Segment!
  setStandFrom(dateStr);
  setStandTo(dateStr);

    // NEU: Basispreis & Prozent-UI initialisieren
  const base = getBaseUnitPriceFromOrderItem(oiForCell);
  setStandBaseUnitPrice(base);
  setStandPercent('');

  setShowStandModal(true);
  return;
}

  // NEU: Shift+Klick = Abmelden
  if (e.shiftKey && order) {
    openFulfillModalFor(order, item, dateStr);
    return;
  }

   if (offer) {
  // 1) Versuche zuerst das Segment zu finden, das GENAU den Klicktag abdeckt
  const d = toLocalDay(dateStr);
  const oiForCell = offer.offerItems?.find(oi => {
    const sameItem = String(oi.item?.id) === String(item.id);
    if (!sameItem || oi.item?.itemType !== 'rental') return false;
    if (!oi.rentalPeriodFrom || !oi.rentalPeriodTo) return false;
    const from = toLocalDay(oi.rentalPeriodFrom);
    const to   = toLocalDay(oi.rentalPeriodTo);
    return d.isSameOrAfter(from, 'day') && d.isSameOrBefore(to, 'day');
  });

  // 2) Fallback: wenn kein exaktes Segment am Klicktag, nimm erstes Miet-Item derselben Maschine
  const chosenOi = oiForCell ||
    offer.offerItems?.find(oi => String(oi.item?.id) === String(item.id) && oi.item?.itemType === 'rental');

  // 3) Wenn wir gar kein Miet-Item finden: wie früher ins Edit
  if (!chosenOi) {
    navigate(`/offers/${offer.id}/edit`);
    return;
  }

  // 4) Kontext für das neue Angebots-Hauptmodal setzen und öffnen
  setActionOffer(offer);
  setActionOfferItem(chosenOi);
  setActionOfferMachine(item);
  setActionOfferDateStr(dateStr);
  setShowOfferActionModal(true);
  return;
}


  if (order) {
  // Standard-Klick: Haupt-Modal statt sofort Swap
  // Segment finden, das GENAU diesen Tag abdeckt
  const d = toLocalDay(dateStr);
  const oiForCell = order.orderItems?.find(oi => {
    const sameItem =
      String(oi.itemId) === String(item.id) ||
      String(oi.item?.id) === String(item.id);
    if (!sameItem || oi.item?.itemType !== 'rental') return false;
    if (!oi.rentalPeriodFrom || !oi.rentalPeriodTo) return false;
    const from = toLocalDay(oi.rentalPeriodFrom);
    const to   = toLocalDay(oi.rentalPeriodTo);
    return d.isSameOrAfter(from, 'day') && d.isSameOrBefore(to, 'day');
  });

  if (!oiForCell) {
    // Kein Segment exakt am Tag → direkt ins Edit (Fallback)
    navigate(`/orders/${order.id}/edit`);
    return;
  }

  // Kontext für das Haupt-Modal setzen
  setActionOrder(order);
  setActionOrderItem(oiForCell);
  setActionMachine(item);          // Maschinenzeilen-Objekt
  setActionDateStr(dateStr);       // geklickter Tag
  setShowActionModal(true);
  return;
}



};


    
    const customerName = isOffer ? offer?.customer?.company : order?.customer?.company;
    const docNumber = isOffer ? offer?.offerNumber : order?.orderNumber;

    // ⬇️ Miet-OrderItem dieser Maschine finden (nur bei Aufträgen)
const oiForThisMachine = !isOffer && order
  ? order.orderItems?.find(
      (oi) =>
        String(oi.itemId) === String(item.id) ||
        String(oi.item?.id) === String(item.id)
    )
  : null;

const fulfilledBy = oiForThisMachine?.fulfilledBy || null;
const fulfilledAt = oiForThisMachine?.fulfillmentDate
  ? moment(oiForThisMachine.fulfillmentDate).format('DD.MM.YYYY')
  : null;
const fulfilledAtTime = oiForThisMachine?.fulfillmentDate
  ? moment(oiForThisMachine.fulfillmentDate).format('HH:mm')
  : null;

// ── NEU: 5 Service-Felder (UG/RN) fürs Tooltip
const ugDateRaw = oiForThisMachine?.ugDate ? moment(oiForThisMachine.ugDate) : null;
const rnDateRaw = oiForThisMachine?.rnDate ? moment(oiForThisMachine.rnDate) : null;

const ugDateStr  = ugDateRaw ? ugDateRaw.format('DD.MM.YYYY') : null;
const ugTimeStr  = ugDateRaw ? ugDateRaw.format('HH:mm') : null;
const rnDateStr  = rnDateRaw ? rnDateRaw.format('DD.MM.YYYY') : null;
const rnTimeStr  = rnDateRaw ? rnDateRaw.format('HH:mm') : null;

const ugBy   = oiForThisMachine?.ugBy   || null;
const ugRnNr = oiForThisMachine?.ugRnNr || null;
const rnBy   = oiForThisMachine?.rnBy   || null;


const nativeTitle = !isPortal
  ? [
      
      !isOffer && fulfilledAt ? `Abgemeldet am: ${fulfilledAt}${fulfilledAtTime ? ' ' + fulfilledAtTime + ' Uhr' : ''}` : null,
      !isOffer && fulfilledBy ? `Abgemeldet von: ${fulfilledBy}` : null,
    ].filter(Boolean).join('\n')
  : undefined;


    
    return (
      <div
        key={index}
        className={`reservation-block ${finalClass}`}
        onClick={handleClick}
        onMouseEnter={!isPortal ? (e) => showCalendarTooltip(e,
          <>
            <strong>Kunde:</strong> {customerName || 'Unbekannt'}<br />
            <strong>{isOffer ? 'Angebot' : 'Auftrag'}:</strong> {docNumber}<br />
            {!isOffer && (ugDateStr || ugBy || ugRnNr) && (
              <>
                <strong>UG:</strong> {ugDateStr || '—'}
                {ugTimeStr ? ` ${ugTimeStr} Uhr` : ''}<br />
                {ugBy && (<><strong>UG durch:</strong> {ugBy}<br /></>)}
                {ugRnNr && (<><strong>Nr.:</strong> {ugRnNr}<br /></>)}
              </>
            )}
            {!isOffer && (rnDateStr || rnBy) && (
              <>
                <strong>RN:</strong> {rnDateStr || '—'}
                {rnTimeStr ? ` ${rnTimeStr} Uhr` : ''}<br />
                {rnBy && (<><strong>RN durch:</strong> {rnBy}<br /></>)}
              </>
            )}
          </>
        ) : undefined}
        onMouseLeave={!isPortal ? hideCalendarTooltip : undefined}
        title={nativeTitle}
        style={portalVisualStyle}                  // ⬅️ WICHTIG: macht eigene Aufträge blau
      data-own-order={isOwnOrder || undefined}   // optional: zum Debuggen in DevTools
      data-own-reservation={isOwnReservation || undefined}
      >
        {/* Der customerCode wird in einem separaten <span> gerendert */}
        <span>{customerCode}</span>
        
      </div>
    );

    
  } else if (event.type === 'workshop') {
    // Workshop-Logik
    const blockColorClass = 'workshop-blocked-day';
    const displayText = event.customerCode || 'WSA';
    const handleClick = () => {
       if (isPortal) return;
      // Der Token wird in der Zielseite (z.B. aus sessionStorage) ausgelesen, falls benötigt
    //  window.open(`${window.location.origin}/workshoporders/${event.workshopId}/edit`,'_blank');
    navigate(`/workshoporders/${event.workshopId}/edit`);
    };


    return (
      <div
      key={index}
      className={`reservation-block ${blockColorClass}`}
      onClick={handleClick}
      onMouseEnter={!isPortal ? (e) => showCalendarTooltip(e,
        <>
          <strong>Werkstattauftrag</strong><br />
          Nummer: {event.workshopOrderNumber}<br />
          Typ: {event.workshopOrderType}<br />
          {event.notes}
        </>
      ) : undefined}
      onMouseLeave={!isPortal ? hideCalendarTooltip : undefined}
      style={{ cursor: isPortal ? 'default' : 'pointer' }}
      // ⬇️ kein nativer Tooltip für Portalnutzer
      {...(!isPortal
        ? { title: `Werkstattauftrag\nNummer: ${event.workshopOrderNumber}\nTyp: ${event.workshopOrderType}${event.notes ? `\n${event.notes}` : ''}` }
        : {})}
    >
      {displayText}
    </div>
    );
  }
})



  ) : (
    // Fallback, wenn weder Reservierungen noch Workshopdaten vorliegen
    <div
  className={`reservation-block ${cellClass}`}
  onClick={() => {
    if (cellClass.includes('free-day')) {
      if (item.itemType === 'rental') {
        if (isPortal) {
          // Portal: wie bisher dein Reservierungs-Modal
          setModalMachine(item);
          setModalDateStr(dateStr);
          setModalDays(1);
          setShowPortalModal(true);
          setIsCreatingReservation(false);
          setReservationSaved(false);
        } else {
          // Intern: NEU – Auswahl-Modal (Auftrag/Angebot)
          setChoiceMachine(item);
          setChoiceDateStr(dateStr);
          setShowCreateChoiceModal(true);
        }
      } else if (
        item.itemType === 'transportvehicle' ||
        item.itemType === 'inventory'
      ) {
        if (!isPortal) createWorkshopDraftOrder(item.id, dateStr);
      } else {
        console.log("Kein passender Draft-Typ definiert für itemType:", item.itemType);
      }
    }
  }}
>
  {/* Keine Events */}
</div>



  )}
</div>


                 {/* Gemeinsame untere Teilzelle für Transport- und Servicemarker */}
{/* Gemeinsame untere Teilzelle für Transport- und Servicemarker */}
<div className="sub-cell" style={{ flex: '0 0 20%' }}>
  {!isPortal && (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap', // Erzwingt, dass keine Zeilenumbrüche erfolgen
        alignItems: 'center',
        gap: '2px',        // Abstand zwischen den Markern; anpassen nach Bedarf
        height: '100%',    // Höhe des Containers entspricht der sub-cell-Höhe (15px)
        overflow: 'hidden' // Überschüssige Marker werden nicht den Container erweitern
      }}
    >
      {/* Transportmarker */}
      {item.transportAvailability?.[dateStr]?.map((marker, index) => (
        <div
          key={`transport-${index}`}
          className={`transport-marker ${marker.type === 'delivery' ? 'delivery-marker' : 'pickup-marker'}`}
          title={`${marker.type === 'delivery' ? 'Lieferung' : 'Abholung'}: ${dateStrPretty}\nAuftragsnummer: ${marker.orderNumber}`}
          onClick={(e) => {
            e.stopPropagation();
            // Für interne Nutzer klickbar:
            navigate(`/orders/${marker.orderId}/transportmarkers`);
          }}
          style={{ cursor: 'pointer' }}
        ></div>
      ))}

      {/* Servicemarker */}
      {item.serviceAvailability?.[dateStr]?.map((marker, index) => {
        // Zeit-String passend auslesen und anzeigen
        let timeString = '';
        if (marker.type === 'UG' && marker.UGTime) {
          timeString = ' – ' + marker.UGTime.slice(0, 5) + ' Uhr';
        }
        if (marker.type === 'RN' && marker.RNTime) {
          timeString = ' – ' + marker.RNTime.slice(0, 5) + ' Uhr';
        }

        return (
          <div
            key={`service-${index}`}
            className={`service-marker ${marker.type === 'UG' ? 'ug-marker' : 'rn-marker'}`}
            title={
              marker.type === 'UG'
                ? `Service Start (UG): ${dateStrPretty}${timeString}\nAuftragsnummer: ${marker.orderNumber}`
                : `Service Ende (RN): ${dateStrPretty}${timeString}\nAuftragsnummer: ${marker.orderNumber}`
            }
            onClick={(e) => {
              e.stopPropagation();
              // Für interne Nutzer klickbar:
              navigate(`/orders/${marker.orderId}/servicemarkers`);
            }}
            style={{ cursor: 'pointer' }}
          ></div>
        );
      })}

      {/* ┌─────────────────────────────────────────────────────────────── */}
      {/* │ 3B) NEU: Prüfungs-Marker (Gelb) einfügen                     */}
      {/* └─────────────────────────────────────────────────────────────── */}
      {item.inspectionAvailability?.[dateStr]?.map((inspectionEvent, idx) => {
        const titleText = `Prüfung (${inspectionEvent.subtype}): ${date.format('DD.MM.YYYY')}`;

        const handleClick = (e) => {
          e.stopPropagation();
          // Für interne Nutzer: Werkstatt-Entwurf anlegen
          createWorkshopDraftOrder(item.id, dateStr, inspectionEvent.subtype);
        };


        return (
          <div
            key={`inspection-${idx}`}
            className="inspection-marker"
            onClick={handleClick}
            style={{ cursor: 'pointer' }}
            title={titleText}
          >
            {/* eigenes Tooltip nur für interne Nutzer */}
            <span className="inspection-tooltip">
              {inspectionEvent.subtype}-Prüfung<br />
              {dateStrPretty}
            </span>
          </div>
        );
      })}
</div>
 )}

  </div>

      </td>
            );
          })}
        </tr>
      );
    });
  };

  if (isLoading) {
    return <div>Daten werden geladen...</div>;
  }

  if (rentalData.length === 0) {
    return <div>Keine Daten verfügbar.</div>;
  }

  return (
    <div className={`calendar-container ${isPortal ? 'portal-user' : ''}`} style={{ padding: '0px' }}>
      <Helmet>
        <title>Kalender</title>
        <meta name="description" content="Übersichtlicher Kalender zur Verwaltung von Mietgeräten." />
      </Helmet>
      <CalendarTooltip tooltip={calendarTooltip} />
    <h2></h2>
      {/* Logo: groß, mit Abstand, ohne Layout zu vergrößern */}
    {brandSignature && (
      <img
        src={brandSignature}
        alt="Brand Logo"
        style={{
      position: 'absolute',
      top: 80,          // 80px
      right: 25,        // 16px
      height: 40,      // 110px fix
      width: 'auto',
      objectFit: 'contain',
      pointerEvents: 'none',
      zIndex: 1
        }}
      />
    )}

{/* Portal-Reservierungs-Modal im Pipeline-Style */}
<SimpleModal
  open={showPortalModal}
  onClose={() => {
    setShowPortalModal(false);
    setIsCreatingReservation(false);
    setReservationSaved(false);
    setCreateJustSaved(false);
    setCreateTotalNet(null);
  }}
>
  <h3>Reservierung anlegen</h3>
  <p>
    <strong>Maschine:</strong> {modalMachine?.name}<br/>
    <strong>Start:</strong> {moment(modalDateStr).format('DD.MM.YYYY')}
  </p>

  <div style={{ display: 'grid', gap: 12 }}>
    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ minWidth: 120 }}>Anzahl Tage:</span>
      <input
        type="number"
        min={1}
        value={modalDays}
        onChange={e => setModalDays(e.target.value)}
        style={{ width: 120 }}
      />
    </label>

    <div className="checkbox-row">
  <label className="checkbox-label">
    <input
      type="checkbox"
      checked={modalIncludeSaturday}
      onChange={e => setModalIncludeSaturday(e.target.checked)}
    />
    <span>Samstag zählen</span>
  </label>

  <label className="checkbox-label">
    <input
      type="checkbox"
      checked={modalIncludeSunday}
      onChange={e => setModalIncludeSunday(e.target.checked)}
    />
    <span>Sonntag zählen</span>
  </label>

  <label className="checkbox-label">
    <input
      type="checkbox"
      checked={modalIncludeHolidays}
      onChange={e => setModalIncludeHolidays(e.target.checked)}
    />
    <span>Feiertage zählen</span>
  </label>
</div>

    {/* Einsatzort */}
<label style={{ display: 'grid', gap: 6 , marginTop: '10px'}}>
  <span>Einsatzort:</span>
  <input
    type="text"
    value={modalOperationLocation}
    onChange={e => setModalOperationLocation(e.target.value)}
    placeholder="z. B. Musterstraße 1, 30159 Hannover"
    style={{ width: '100%' }}
    maxLength={255}
  />
</label>

{/* Interne Notiz */}
<label style={{ display: 'grid', gap: 6 }}>
  <span>Interne Notiz:</span>
  <textarea
    value={modalInternalNote}
    onChange={e => setModalInternalNote(e.target.value)}
    placeholder="Transporte, Zusatzartikel etc. hier anfragen"
    rows={3}
    style={{ width: '100%' }}
    maxLength={2000}
  />
</label>

{/* Preis nach dem Speichern */}
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
  <span style={{ opacity: 0.8 }}>Gesamtpreis (netto):</span>
  <strong>
    {createTotalNet != null
      ? createTotalNet.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
      : '—'}
  </strong>
</div>

{createJustSaved && (
  <div style={{
    marginTop: 8,
    padding: '8px 10px',
    background: '#e6fcf5',
    border: '1px solid #c3fae8',
    borderRadius: 6,
    color: '#087f5b',
    fontWeight: 600
  }}>
    Reservierung gespeichert.
  </div>
)}

  </div>

  <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
  <button
    onClick={() => setShowPortalModal(false)}
    style={{ background: "#eee", color: "#333", padding: "8px 16px", border: "none", borderRadius: 5 }}
  >
    {reservationSaved ? 'Schließen' : 'Abbrechen'}
  </button>

  <button
    onClick={submitPortalReservation}
    disabled={isCreatingReservation || reservationSaved}  // ← verhindert zweiten Klick
    style={{
      background: reservationSaved ? "#94d82d" : "#2eae4a",
      opacity: (isCreatingReservation || reservationSaved) ? 0.7 : 1,
      cursor: (isCreatingReservation || reservationSaved) ? 'not-allowed' : 'pointer',
      color: "#fff", padding: "8px 16px", border: "none", borderRadius: 5
    }}
  >
    {isCreatingReservation ? 'Reserviere…' : (reservationSaved ? 'Gespeichert' : 'Reservieren')}
  </button>
</div>

</SimpleModal>

{/* Edit-Modal für Portal-Reservierungen – jetzt mit SimpleModal */}
<SimpleModal
  open={showEditReservationModal}
  onClose={() => setShowEditReservationModal(false)}
>
  <h3>Reservierung bearbeiten</h3>

  <p style={{ marginBottom: 8 }}>
    <strong>Maschine:</strong> {editOfferItem?.item?.name || '-'}<br />
    <strong>Start:</strong>{' '}
    {editOfferItem?.rentalPeriodFrom
      ? moment(editOfferItem.rentalPeriodFrom).format('DD.MM.YYYY')
      : '-'}
  </p>

  <div style={{ display: 'grid', gap: 12 }}>
    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ minWidth: 120 }}>Anzahl Tage:</span>
      <input
        type="number"
        min={1}
        value={editDays}
        onChange={e => setEditDays(e.target.value)}
        style={{ width: 120 }}
      />
    </label>
    <div className="checkbox-row">
    <label className="checkbox-label">
      <input
        type="checkbox"
        checked={editIncludeSaturday}
        onChange={e => setEditIncludeSaturday(e.target.checked)}
      />
      Samstag zählen
    </label>

    <label className="checkbox-label">
      <input
        type="checkbox"
        checked={editIncludeSunday}
        onChange={e => setEditIncludeSunday(e.target.checked)}
      />
      Sonntag zählen
    </label>

    <label className="checkbox-label">
      <input
        type="checkbox"
        checked={editIncludeHolidays}
        onChange={e => setEditIncludeHolidays(e.target.checked)}
      />
      Feiertage zählen
    </label>
    </div>

    {/* Einsatzort */}
<label style={{ display: 'grid', gap: 6 }}>
  <span>Einsatzort:</span>
  <input
    type="text"
    value={editOperationLocation}
    onChange={e => setEditOperationLocation(e.target.value)}
    placeholder="z. B. Musterstraße 1, 30159 Hannover"
    style={{ width: '100%' }}
    maxLength={255}
  />
</label>

{/* Interne Notiz */}
<label style={{ display: 'grid', gap: 6 }}>
  <span>Interne Notiz:</span>
  <textarea
    value={editInternalNote}
    onChange={e => setEditInternalNote(e.target.value)}
    placeholder="Transporte, Zusatzartikel etc. hier anfragen"
    rows={3}
    style={{ width: '100%' }}
    maxLength={2000}
  />
</label>

{/* Aktueller Nettogesamtpreis */}
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
  <span style={{ opacity: 0.8 }}>Maschinenpreis (netto) zzgl. MBV:</span>
  <strong>
    {editTotalNet != null
      ? editTotalNet.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
      : '—'}
  </strong>
</div>

{editJustSaved && (
  <div style={{
    marginTop: 8,
    padding: '8px 10px',
    background: '#e6fcf5',
    border: '1px solid #c3fae8',
    borderRadius: 6,
    color: '#087f5b',
    fontWeight: 600
  }}>
    Änderungen gespeichert.
  </div>
)}

  </div>

  <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
    <button
      onClick={() => setShowEditReservationModal(false)}
      style={{ background: '#eee', color: '#333', padding: '8px 16px', border: 'none', borderRadius: 5 }}
    >
      Abbrechen
    </button>
    <button
  onClick={saveEditedReservation}
  disabled={isSavingEdit}  // ➕ während Request sperren
  style={{
    background: '#2eae4a',
    opacity: isSavingEdit ? 0.7 : 1,
    cursor: isSavingEdit ? 'not-allowed' : 'pointer',
    color: '#fff', padding: '8px 16px', border: 'none', borderRadius: 5
  }}
>
  {isSavingEdit ? 'Speichere…' : 'Speichern'}
</button>
  </div>
</SimpleModal>

<SimpleModal
  open={showSwapModal}
  onClose={() => setShowSwapModal(false)}
>
  <h3>Maschine ab Datum tauschen</h3>

  <div style={{ display: 'grid', gap: 12 }}>
    <div style={{fontSize: 14, opacity:.8}}>
      <div><strong>Auftrag:</strong> {swapOrder?.orderNumber || swapOrder?.id}</div>
      <div><strong>Aktuelle Maschine:</strong> {swapOrderItem?.item?.name}</div>
      {swapOrderItem?.rentalPeriodFrom && swapOrderItem?.rentalPeriodTo && (
        <div>
          <strong>aktueller Zeitraum:</strong>{' '}
          {moment(swapOrderItem.rentalPeriodFrom).format('DD.MM.YYYY')}
          {' — '}
          {moment(swapOrderItem.rentalPeriodTo).format('DD.MM.YYYY')}
        </div>
      )}
    </div>

    {/* Start-Datum */}
    <label style={{ display: 'grid', gap: 6 }}>
      <span>Ab Datum</span>
      <input
        type="date"
        value={swapStartDate}
        onChange={e => setSwapStartDate(e.target.value)}
      />
    </label>

    {/* Einfache Maschinenauswahl: Datalist aus vorhandenen rentalData */}
    <label style={{ display:'grid', gap:6 }}>
      <span>Neue Maschine</span>
      <input
        type="text"
        list="swap-machine-options"
        placeholder="Maschine suchen…"
        onChange={e => {
          const name = e.target.value;
          const m = rentalData.find(x => x.name === name);
          setSwapNewItem(m ? { id: m.id, name: m.name } : null);
        }}
      />
      <datalist id="swap-machine-options">
        {rentalData
          .filter(x => x.itemType === 'rental')
          .map(m => <option key={m.id} value={m.name} />)}
      </datalist>
    </label>

    <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:8 }}>
      <button
        onClick={() => {
          setShowSwapModal(false);
          // Abbrechen ⇒ wie bisher ins Edit
          if (swapOrder) 
          //  window.open(`${window.location.origin}/orders/${swapOrder.id}/edit`, '_blank');
        navigate(`/orders/${swapOrder.id}/edit`);
        }}
        style={{ background:'#eee', color:'#333', padding:'8px 16px', border:'none', borderRadius:5 }}
      >
        Auftrag bearbeiten
      </button>
      <button
        onClick={async () => {
          if (!swapOrder || !swapOrderItem || !swapNewItem || !swapStartDate) return;
          try {
  // ⛔ SPERRE: Maschinentausch nur bis zum ersten Standtag
  const cutYMD = swapStartDate.slice(0, 10);

  const hasStandtagAfter = swapOrder.orderItems?.some(oi =>
    oi.item?.name === 'Standtag' &&
    oi.rentalPeriodFrom &&
    oi.rentalPeriodFrom.slice(0, 10) >= cutYMD &&
    oi.mainItemId === swapOrderItem.id
  );

  if (hasStandtagAfter) {
    alert('Maschinentausch nur bis (inkl.) Standtag möglich.');
   
  }

  setIsSwapping(true);

  // 1) Server: Swap ausführen
  await api.post(`/orders/${swapOrder.id}/swap-rental`, {
    orderItemId: swapOrderItem.id,
    newItemId: swapNewItem.id,
    fromDate: swapStartDate
  });

            // 2) Modal zu, Kalender refresh und ins Edit
            setShowSwapModal(false);
            setReloadTick(t => t + 1);
        //    window.open(`${window.location.origin}/orders/${swapOrder.id}/edit`, '_blank');
            
            navigate(`/orders/${swapOrder.id}/edit`);
          } catch (e) {
            console.error('Swap fehlgeschlagen', e);
            alert('Tausch konnte nicht durchgeführt werden.');
          } finally {
            setIsSwapping(false);
          }
        }}
        disabled={isSwapping || !swapNewItem || !swapStartDate}
        style={{
          background:'#2eae4a',
          opacity: (isSwapping || !swapNewItem || !swapStartDate) ? .7 : 1,
          cursor: (isSwapping || !swapNewItem || !swapStartDate) ? 'not-allowed' : 'pointer',
          color:'#fff', padding:'8px 16px', border:'none', borderRadius:5
        }}
      >
        {isSwapping ? 'Tausche…' : 'Tauschen & Öffnen'}
      </button>
    </div>
  </div>
</SimpleModal>

<SimpleModal
  open={showSwapOfferModal}
  onClose={() => setShowSwapOfferModal(false)}
>
  <h3>Maschine ab Datum tauschen (Angebot)</h3>

  <div style={{ display: 'grid', gap: 12 }}>
    <div style={{fontSize: 14, opacity:.8}}>
      <div><strong>Angebot:</strong> {swapOffer?.offerNumber || swapOffer?.id}</div>
      <div><strong>Aktuelle Maschine:</strong> {swapOfferItem?.item?.name}</div>
      {swapOfferItem?.rentalPeriodFrom && swapOfferItem?.rentalPeriodTo && (
        <div>
          <strong>aktueller Zeitraum:</strong>{' '}
          {moment(swapOfferItem.rentalPeriodFrom).format('DD.MM.YYYY')}
          {' — '}
          {moment(swapOfferItem.rentalPeriodTo).format('DD.MM.YYYY')}
        </div>
      )}
    </div>

    {/* Start-Datum */}
    <label style={{ display: 'grid', gap: 6 }}>
      <span>Ab Datum</span>
      <input
        type="date"
        value={swapOfferStartDate}
        onChange={e => setSwapOfferStartDate(e.target.value)}
      />
    </label>

    {/* neue Maschine auswählen */}
    <label style={{ display:'grid', gap:6 }}>
      <span>Neue Maschine</span>
      <input
        type="text"
        list="swap-offer-machine-options"
        placeholder="Maschine suchen…"
        onChange={e => {
          const name = e.target.value;
          const m = rentalData.find(x => x.name === name);
          setSwapOfferNewItem(m ? { id: m.id, name: m.name } : null);
        }}
      />
      <datalist id="swap-offer-machine-options">
        {rentalData
          .filter(x => x.itemType === 'rental')
          .map(m => <option key={m.id} value={m.name} />)}
      </datalist>
    </label>

    <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:8 }}>
      <button
        onClick={() => {
          setShowSwapOfferModal(false);
          if (swapOffer) navigate(`/offers/${swapOffer.id}/edit`);
        }}
        style={{ background:'#eee', color:'#333', padding:'8px 16px', border:'none', borderRadius:5 }}
      >
        Angebot bearbeiten
      </button>
      <button
        onClick={async () => {
          if (!swapOffer || !swapOfferItem || !swapOfferNewItem || !swapOfferStartDate) return;
          try {
            setIsSwappingOffer(true);
            await api.post(`/offers/${swapOffer.id}/swap-rental`, {
              offerItemId: swapOfferItem.id,
              newItemId: swapOfferNewItem.id,
              fromDate: swapOfferStartDate
            });
            setShowSwapOfferModal(false);
            setReloadTick(t => t + 1);
            navigate(`/offers/${swapOffer.id}/edit`);
          } catch (e) {
            console.error('Offer-Swap fehlgeschlagen', e);
            alert('Tausch im Angebot konnte nicht durchgeführt werden.');
          } finally {
            setIsSwappingOffer(false);
          }
        }}
        disabled={isSwappingOffer || !swapOfferNewItem || !swapOfferStartDate}
        style={{
          background:'#2eae4a',
          opacity: (isSwappingOffer || !swapOfferNewItem || !swapOfferStartDate) ? .7 : 1,
          cursor: (isSwappingOffer || !swapOfferNewItem || !swapOfferStartDate) ? 'not-allowed' : 'pointer',
          color:'#fff', padding:'8px 16px', border:'none', borderRadius:5
        }}
      >
        {isSwappingOffer ? 'Tausche…' : 'Tauschen & Öffnen'}
      </button>
    </div>
  </div>
</SimpleModal>



<SimpleModal
  open={showFulfillModal}
  onClose={() => {
    setShowFulfillModal(false);
    setFulfillOrder(null);
    setFulfillOrderItem(null);
    setFulfillContacts([]);
    setFulfillContactId('');
    setIsFulfilling(false);
  }}
>
  <h3>Maschine abmelden</h3>
  <p style={{ marginBottom: 8 }}>
    <strong>Auftrag:</strong> {fulfillOrder?.orderNumber || fulfillOrder?.id || '—'}<br/>
    <strong>Maschine:</strong> {fulfillOrderItem?.item?.name || '—'}
  </p>

  <div style={{ display: 'grid', gap: 12 }}>
   <label style={{ display: 'grid', gap: 6 }}>
  <span>Abmeldedatum</span>
  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
    <input
    type="datetime-local"
    value={fulfillDate}
    onChange={e => setFulfillDate(e.target.value)}
    required
  />
    
  </div>
</label>

<label style={{ display: 'grid', gap: 6 }}>
  <span>Ansprechpartner (Kunde)</span>
  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
    <select
      value={fulfillContactId}
      onChange={e => setFulfillContactId(e.target.value)}
      style={{ marginLeft: 0 }}
    >
      <option value="">Bitte wählen…</option>
      {fulfillContacts.map(c => {
  const baseName =
    [c.contactFirstName, c.contactLastName].filter(Boolean).join(' ')
    || c.contactEmail
    || `Kontakt #${c.id}`;

  const label = `${baseName}${suffixByContactFunction(c.contactFunction)}`;
  return <option key={c.id} value={c.id}>{label}</option>;
})}

    </select>
    
  </div>
</label>

  </div>

  <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
  {/* Abbrechen */}
  <button
    onClick={() => setShowFulfillModal(false)}
    style={{ background: '#eee', color: '#333', padding: '8px 16px', border: 'none', borderRadius: 5 }}
    disabled={isFulfilling}
  >
    Abbrechen
  </button>

  {/* Zurücksetzen: setzt beide Felder (Datum & Ansprechpartner) serverseitig auf null */}
  <button
    onClick={async () => {
      if (!fulfillOrder || !fulfillOrderItem) return;
      try {
        setIsFulfilling(true);
        await api.put(
          `/orders/${fulfillOrder.id}/orderitems/${fulfillOrderItem.id}/fulfill`,
          { fulfillmentDate: null, contactId: null }
        );
        setShowFulfillModal(false);
        setReloadTick(t => t + 1);
      } catch (e) {
        console.error('Zurücksetzen fehlgeschlagen:', e);
        alert('Zurücksetzen fehlgeschlagen.');
      } finally {
        setIsFulfilling(false);
      }
    }}
    disabled={isFulfilling}
    style={{
      background: '#f1f3f5',
      color: '#333',
      padding: '8px 16px',
      border: '1px solid #dee2e6',
      borderRadius: 5
    }}
    title="Datum und Ansprechpartner entfernen"
  >
    Zurücksetzen
  </button>

  {/* Speichern: übernimmt ggf. leere Felder (werden als null gesendet) */}
  <button
    onClick={async () => {
      if (!fulfillOrder || !fulfillOrderItem) return;
      try {
        setIsFulfilling(true);
        const payload = {
          fulfillmentDate: fulfillDate ? fulfillDate : null,
          contactId: fulfillContactId ? fulfillContactId : null
        };
        await api.put(
          `/orders/${fulfillOrder.id}/orderitems/${fulfillOrderItem.id}/fulfill`,
          payload
        );
        setShowFulfillModal(false);
        setReloadTick(t => t + 1);
      } catch (e) {
        console.error('Abmelden/Ändern fehlgeschlagen:', e);
        alert('Abmelden/Ändern fehlgeschlagen.');
      } finally {
        setIsFulfilling(false);
      }
    }}
    disabled={isFulfilling}
    style={{
      background: '#2eae4a',
      color: '#fff',
      padding: '8px 16px',
      border: 'none',
      borderRadius: 5,
      opacity: isFulfilling ? 0.7 : 1,
      cursor: isFulfilling ? 'not-allowed' : 'pointer'
    }}
  >
    {isFulfilling ? 'Speichere…' : 'Speichern'}
  </button>

  </div>
</SimpleModal>

<SimpleModal
  open={showStandModal}
  onClose={() => {
    setShowStandModal(false);
    setStandOrder(null);
    setStandOrderItem(null);
    setStandFrom('');
    setStandTo('');
    setStandManualPrice('');
    setIsSplittingStand(false);
    setStandPercent('');
    setStandBaseUnitPrice(null);
  }}
>
  <h3>Standtage markieren</h3>
  <p style={{ marginBottom: 8 }}>
    <strong>Auftrag:</strong> {standOrder?.orderNumber || standOrder?.id || '—'}<br/>
    <strong>Maschine:</strong> {standOrderItem?.item?.name || '—'}<br/>
    <strong>Standtag-Artikel-ID:</strong> {standItemId ?? '—'}
  </p>

  <div style={{ display:'grid', gap: 12 }}>
    <label style={{ display:'grid', gap: 6 }}>
      <span>Stand von</span>
      <input type="date" value={standFrom} onChange={e=>setStandFrom(e.target.value)} />
    </label>

    <label style={{ display:'grid', gap: 6 }}>
      <span>Stand bis</span>
      <input type="date" value={standTo} onChange={e=>setStandTo(e.target.value)} />
    </label>

    <label style={{ display:'grid', gap: 6 }}>
      <span>Preis pro Standtag (optional, manuell)</span>
      <input
        type="number"
        min="0" step="0.01"
        placeholder="leer lassen = Preisliste vom Artikel „Standtag“"
        value={standManualPrice}
        onChange={e=>setStandManualPrice(e.target.value)}
      />
    </label>
    {standBaseUnitPrice != null && (
  <>
    <div style={{ display:'grid', gap: 6 }}>
      <span style={{ opacity:.8 }}>Tagespreis (Basis)</span>
      <div style={{ padding:'6px 8px', border:'1px solid #ddd', borderRadius:4, background:'#f8f9fa' }}>
        {standBaseUnitPrice.toLocaleString('de-DE', { style:'currency', currency:'EUR' })}
      </div>
    </div>

    <label style={{ display:'grid', gap: 6 }}>
      <span>Prozent (vom Tagespreis)</span>
      <input
        type="number"
        min="0" max="100" step="1"
        placeholder="z. B. 50"
        value={standPercent}
        onChange={e => setStandPercent(e.target.value)}
      />
      <small style={{ opacity:.7 }}>
        Wird automatisch in „Preis pro Standtag“ übernommen.
      </small>
    </label>
  </>
)}
  </div>

  <div style={{ marginTop: 14, display:'flex', gap: 8, justifyContent:'flex-end' }}>
    <button
      onClick={()=>setShowStandModal(false)}
      style={{ background:'#eee', color:'#333', padding:'8px 16px', border:'none', borderRadius:5 }}
      disabled={isSplittingStand}
    >
      Abbrechen
    </button>

    <button
      onClick={async ()=>{
        if (!standOrder || !standOrderItem || !standFrom || !standTo || !standItemId) return;
        try {
          setIsSplittingStand(true);
          await api.post(`/orders/${standOrder.id}/orderitems/${standOrderItem.id}/split-standdays`, {
            standFrom,
            standTo,
            standItemId, // per Name „Standtag“ ermittelt
            manualPricePerDay: standManualPrice
  ? Number(String(standManualPrice).replace(',', '.'))
  : null
          });
          setShowStandModal(false);
          setReloadTick(t => t + 1);   // Kalender aktualisieren
        } catch (e) {
          console.error('Standtage splitten fehlgeschlagen:', e);
          alert('Standtage konnten nicht gespeichert werden.');
        } finally {
          setIsSplittingStand(false);
        }
      }}
      disabled={isSplittingStand}
      style={{
        background:'#2eae4a',
        color:'#fff', padding:'8px 16px',
        border:'none', borderRadius:5,
        opacity: isSplittingStand ? .7 : 1,
        cursor: isSplittingStand ? 'not-allowed' : 'pointer'
      }}
    >
      {isSplittingStand ? 'Speichere…' : 'Speichern'}
    </button>
  </div>
</SimpleModal>

<SimpleModal
  open={showRangeModal}
  onClose={closeRangeModal}
>
  <h3>Mietdauer (Tage) anpassen</h3>

  <p style={{ marginBottom: 8 }}>
    <strong>Auftrag:</strong> {rangeOrder?.orderNumber || rangeOrder?.id || '—'}<br/>
    <strong>Maschine:</strong> {rangeOrderItem?.item?.name || '—'}
  </p>

  <div style={{ display:'grid', gap: 12 }}>
    <label style={{ display:'grid', gap:6 }}>
      <span>Von</span>
      <input
        type="date"
        value={rangeFrom}
        onChange={e => {
          setRangeFrom(e.target.value);
          resetRangePricePrompt();
        }}
      />
    </label>

    <label style={{ display:'grid', gap:6 }}>
      <span>Bis</span>
      <input
        type="date"
        value={rangeTo}
        onChange={e => {
          setRangeTo(e.target.value);
          resetRangePricePrompt();
        }}
      />
    </label>

    <div style={{ fontSize: 12, opacity: .75 }}>
      Tipp: <kbd>Ctrl/Cmd</kbd> + Klick auf einen belegten Tag öffnet dieses Fenster direkt.
      <br />
      Samstage/Sonntage/Feiertage werden gemäß Flags der Position gezählt.
    </div>
  </div>

  <RentalPriceDecisionInfo info={rangePriceDecision} />

  <div style={{ marginTop: 14, display:'flex', gap:8, justifyContent:'flex-end' }}>
    <button
      onClick={closeRangeModal}
      style={{ background:'#eee', color:'#333', padding:'8px 16px', border:'none', borderRadius:5 }}
      disabled={isSavingRange}
    >
      Abbrechen
    </button>

    {rangePriceDecision ? (
      <>
        <button
          onClick={() => saveOrderRentalRange(rangePendingPayload, false)}
          disabled={isSavingRange}
          style={{
            background:'#6c757d',
            color:'#fff', padding:'8px 16px',
            border:'none', borderRadius:5,
            opacity: isSavingRange ? .7 : 1,
            cursor: isSavingRange ? 'not-allowed' : 'pointer'
          }}
        >
          Alten Preis beibehalten
        </button>
        <button
          onClick={() => saveOrderRentalRange(rangePendingPayload, true)}
          disabled={isSavingRange}
          style={{
            background:'#2eae4a',
            color:'#fff', padding:'8px 16px',
            border:'none', borderRadius:5,
            opacity: isSavingRange ? .7 : 1,
            cursor: isSavingRange ? 'not-allowed' : 'pointer'
          }}
        >
          Staffelpreis aktualisieren
        </button>
      </>
    ) : (
      <button
        onClick={previewOrderRentalRange}
        disabled={isSavingRange}
        style={{
          background:'#2eae4a',
          color:'#fff', padding:'8px 16px',
          border:'none', borderRadius:5,
          opacity: isSavingRange ? .7 : 1,
          cursor: isSavingRange ? 'not-allowed' : 'pointer'
        }}
      >
        {isSavingRange ? 'Prüfe…' : 'Speichern'}
      </button>
    )}
  </div>
</SimpleModal>

<SimpleModal
  open={showOfferRangeModal}
  onClose={closeOfferRangeModal}
>
  <h3>Mietdauer (Angebot) anpassen</h3>

  <p style={{ marginBottom: 8 }}>
    <strong>Angebot:</strong> {rangeOffer?.offerNumber || rangeOffer?.id || '—'}<br/>
    <strong>Maschine:</strong> {rangeOfferItem?.item?.name || '—'}
  </p>

  <div style={{ display:'grid', gap: 12 }}>
    <label style={{ display:'grid', gap:6 }}>
      <span>Von</span>
      <input
        type="date"
        value={rangeOfferFrom}
        onChange={e => {
          setRangeOfferFrom(e.target.value);
          resetOfferRangePricePrompt();
        }}
      />
    </label>

    <label style={{ display:'grid', gap:6 }}>
      <span>Bis</span>
      <input
        type="date"
        value={rangeOfferTo}
        onChange={e => {
          setRangeOfferTo(e.target.value);
          resetOfferRangePricePrompt();
        }}
      />
    </label>

    <div style={{ fontSize: 12, opacity: .75 }}>
      Tipp: <kbd>Ctrl/Cmd</kbd> + Klick auf einen belegten Tag öffnet dieses Fenster direkt.
    </div>
  </div>

  <RentalPriceDecisionInfo info={offerRangePriceDecision} />

  <div style={{ marginTop: 14, display:'flex', gap:8, justifyContent:'flex-end' }}>
    <button
      onClick={closeOfferRangeModal}
      style={{ background:'#eee', color:'#333', padding:'8px 16px', border:'none', borderRadius:5 }}
      disabled={isSavingOfferRange}
    >
      Abbrechen
    </button>

    {offerRangePriceDecision ? (
      <>
        <button
          onClick={() => saveOfferRentalRange(offerRangePendingPayload, false)}
          disabled={isSavingOfferRange}
          style={{
            background:'#6c757d',
            color:'#fff', padding:'8px 16px',
            border:'none', borderRadius:5,
            opacity: isSavingOfferRange ? .7 : 1,
            cursor: isSavingOfferRange ? 'not-allowed' : 'pointer'
          }}
        >
          Alten Preis beibehalten
        </button>
        <button
          onClick={() => saveOfferRentalRange(offerRangePendingPayload, true)}
          disabled={isSavingOfferRange}
          style={{
            background:'#2eae4a',
            color:'#fff', padding:'8px 16px',
            border:'none', borderRadius:5,
            opacity: isSavingOfferRange ? .7 : 1,
            cursor: isSavingOfferRange ? 'not-allowed' : 'pointer'
          }}
        >
          Staffelpreis aktualisieren
        </button>
      </>
    ) : (
      <button
        onClick={previewOfferRentalRange}
        disabled={isSavingOfferRange}
        style={{
          background:'#2eae4a',
          color:'#fff', padding:'8px 16px',
          border:'none', borderRadius:5,
          opacity: isSavingOfferRange ? .7 : 1,
          cursor: isSavingOfferRange ? 'not-allowed' : 'pointer'
        }}
      >
        {isSavingOfferRange ? 'Prüfe…' : 'Speichern'}
      </button>
    )}
  </div>
</SimpleModal>


<SimpleModal
  open={showCreateChoiceModal}
  onClose={() => {
    setShowCreateChoiceModal(false);
    setChoiceMachine(null);
    setChoiceDateStr(null);
  }}
>
  <h3>Was möchtest du anlegen?</h3>
  <p style={{marginTop: 4, marginBottom: 14}}>
    <strong>Maschine:</strong> {choiceMachine?.name || '—'}<br/>
    <strong>Start:</strong> {choiceDateStr ? moment(choiceDateStr).format('DD.MM.YYYY') : '—'}
  </p>
    {/* Kunde wählen (Pflicht) */}
<div style={{ marginBottom: 16 }}>
  <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>Kunde</label>
  <AsyncSelect
  cacheOptions
  defaultOptions={false}              // erst bei Eingabe suchen
  loadOptions={loadCustomerOptions}
  value={selectedCustomerOpt}         // useState für {value,label}
  onChange={(opt) => setSelectedCustomerOpt(opt)}
  formatOptionLabel={formatCalendarCustomerOptionLabel}
  placeholder="Name, Kd.-Nr., Deb.-Nr. oder Ort…"
  isClearable
  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
  menuPosition="fixed"
  styles={{
    container: (b) => ({ ...b, minWidth: 360 }),
    menuPortal: (b) => ({ ...b, zIndex: 99999 }),
    menu:       (b) => ({ ...b, zIndex: 99999 }),
  }}
/>
  {!selectedCustomerOpt && (
    <div style={{ marginTop: 6, fontSize: 12, color: '#a00' }}>
      
    </div>
  )}
</div>

  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
    <button
      onClick={() => {
        setShowCreateChoiceModal(false);
      }}
      style={{ background:'#eee', color:'#333', padding:'8px 16px', border:'none', borderRadius:5 }}
    >
      Abbrechen
    </button>

    {/* Angebot */}
<button
  onClick={() => {
    if (!choiceMachine || !choiceDateStr) return;
    if (!isPortal && !selectedCustomerOpt) {
      alert('Bitte zuerst einen Kunden wählen.');
      return;
    }
    setShowCreateChoiceModal(false);

    if (isPortal) {
      // Portal: bestehendes Reservierungs-Modal
      setModalMachine(choiceMachine);
      setModalDateStr(choiceDateStr);
      setModalDays(1);
      setShowPortalModal(true);
      setIsCreatingReservation(false);
      setReservationSaved(false);
    } else {
      // Intern: Draft-Angebot mit customerId anlegen
      createDraftOffer(
        choiceMachine.id,           // itemId
        choiceMachine.name,         // optional (falls genutzt)
        choiceDateStr,              // Startdatum
        selectedCustomerOpt.value   // customerId
      );
    }
  }}
  style={{ background:'#6c757d', color:'#fff', padding:'8px 16px', border:'none', borderRadius:5 }}
>
  Angebot
</button>

    {/* Auftrag */}
<button
  onClick={() => {
    if (!choiceMachine || !choiceDateStr) return;
    if (!selectedCustomerOpt) {
      alert('Bitte zuerst einen Kunden wählen.');
      return;
    }

    setShowCreateChoiceModal(false);

    // Direkt Auftrag anlegen (customerId aus selectedCustomerOpt)
    createDraftOrder(
      selectedCustomerOpt.value,   // customerId
      choiceMachine.id,            // itemId
      choiceMachine.name,          // optional, falls genutzt
      choiceDateStr                // Startdatum (YYYY-MM-DD)
    );
  }}
  style={{ background:'#2eae4a', color:'#fff', padding:'8px 16px', border:'none', borderRadius:5 }}
>
  Auftrag
</button>
  </div>
</SimpleModal>

<SimpleModal
  open={showActionModal}
  onClose={() => {
    setShowActionModal(false);
    setActionOrder(null);
    setActionOrderItem(null);
    setActionMachine(null);
    setActionDateStr(null);
  }}
>
  <h3>Was möchtest du tun?</h3>
  <p style={{ marginTop: 4, marginBottom: 14 }}>
    <strong>Auftrag:</strong> {actionOrder?.orderNumber || actionOrder?.id || '—'}<br/>
    <strong>Maschine:</strong> {actionMachine?.name || '—'}
  </p>

  <div style={{ display:'grid', gap:8, marginTop:6 }}>
    <div style={{ fontWeight:600, textAlign:'left' }}>Druckansichten</div>
    <div style={{
      display:'grid',
      gridTemplateColumns:'repeat(auto-fit, minmax(145px, 1fr))',
      gap:8,
      paddingBottom:10,
      marginBottom:2,
      borderBottom:'1px solid #ddd'
    }}>
      <button
        type="button"
        onClick={() => {
          if (actionOrder?.id) openCalendarPdf(`order/${actionOrder.id}/pdf`);
        }}
        style={{
          padding:'10px 12px', borderRadius:8, border:'none',
          background:'#28a745', color:'#fff', cursor:'pointer', fontSize:14
        }}
      >
        Auftrag als PDF
      </button>
      <button
        type="button"
        disabled={!actionOrder?.id || !actionOrderItem?.id}
        onClick={() => openCalendarDeliveryNote(actionOrder?.id, actionOrderItem?.id, 'ls1')}
        title="Lieferschein 1 für die angeklickte Maschine"
        style={{
          padding:'10px 12px', borderRadius:8, border:'none',
          background:'#6c757d', color:'#fff',
          cursor: actionOrderItem?.id ? 'pointer' : 'not-allowed',
          opacity: actionOrderItem?.id ? 1 : 0.55,
          fontSize:14
        }}
      >
        LS1 · diese Maschine
      </button>
      <button
        type="button"
        disabled={!actionOrder?.id || !actionOrderItem?.id}
        onClick={() => openCalendarDeliveryNote(actionOrder?.id, actionOrderItem?.id, 'ls2')}
        title="Lieferschein 2 für die angeklickte Maschine"
        style={{
          padding:'10px 12px', borderRadius:8, border:'none',
          background:'#343a40', color:'#fff',
          cursor: actionOrderItem?.id ? 'pointer' : 'not-allowed',
          opacity: actionOrderItem?.id ? 1 : 0.55,
          fontSize:14
        }}
      >
        LS2 · diese Maschine
      </button>
    </div>

    <button
      onClick={() => {
        if (!actionOrder || !actionOrderItem || !actionMachine) return;
        // Swap-Modal vorbereiten
        setSwapOrder(actionOrder);
        setSwapOrderItem(actionOrderItem);
        // Start bevorzugt der geklickte Tag (wenn im Segment), sonst Segment-Start
        const segFrom = actionOrderItem?.rentalPeriodFrom
          ? moment(actionOrderItem.rentalPeriodFrom).format('YYYY-MM-DD')
          : moment().add(1, 'day').format('YYYY-MM-DD');
        const start = actionDateStr || segFrom;
        setSwapStartDate(start);
        setSwapNewItem(null);
        setShowActionModal(false);
        setShowSwapModal(true);
      }}
      style={{
  display:'block',
  width:'100%',
  padding:'10px 14px',
  borderRadius:10,
  border:'1px solid #ddd',
  background:'#fff',
  textAlign:'center',
  cursor:'pointer',
  // ⬇️ fix gegen “unsichtbare” Labels
  color:'#212529',
  fontSize:14,
  fontWeight:500,
  lineHeight:1.2,
  whiteSpace:'normal',
  textIndent: 0
}}

    >
      Maschine ab Datum tauschen
    </button>

    <button
      onClick={() => {
        if (!actionOrder || !actionOrderItem || !actionMachine) return;
        // Range-Modal vorbereiten
        setRangeOrder(actionOrder);
        setRangeOrderItem(actionOrderItem);
        setRangeFrom(moment(actionOrderItem.rentalPeriodFrom).format('YYYY-MM-DD'));
        setRangeTo(moment(actionOrderItem.rentalPeriodTo).format('YYYY-MM-DD'));
        setShowActionModal(false);
        setShowRangeModal(true);
      }}
      style={{
  display:'block',
  width:'100%',
  padding:'10px 14px',
  borderRadius:10,
  border:'1px solid #ddd',
  background:'#fff',
  textAlign:'center',
  cursor:'pointer',
  // ⬇️ fix gegen “unsichtbare” Labels
  color:'#212529',
  fontSize:14,
  fontWeight:500,
  lineHeight:1.2,
  whiteSpace:'normal',
  textIndent: 0
}}

    >
      Mietdauer (Tage) anpassen
    </button>

    <button
      onClick={() => {
        if (!actionOrder || !actionOrderItem || !actionMachine) return;
        // Standtage-Modal vorbereiten (Standard: geklickter Tag)
        const ymd = actionDateStr || moment(actionOrderItem.rentalPeriodFrom).format('YYYY-MM-DD');
        setStandOrder(actionOrder);
        setStandOrderItem(actionOrderItem);
        setStandFrom(ymd);
        setStandTo(ymd);
        setShowActionModal(false);

        const base = getBaseUnitPriceFromOrderItem(actionOrderItem);
setStandBaseUnitPrice(base);
setStandPercent('');

        setShowStandModal(true);
      }}
      style={{
  display:'block',
  width:'100%',
  padding:'10px 14px',
  borderRadius:10,
  border:'1px solid #ddd',
  background:'#fff',
  textAlign:'center',
  cursor:'pointer',
  // ⬇️ fix gegen “unsichtbare” Labels
  color:'#212529',
  fontSize:14,
  fontWeight:500,
  lineHeight:1.2,
  whiteSpace:'normal',
  textIndent: 0
}}

    >
      Standtage markieren
    </button>

    <button
      onClick={() => {
        if (!actionOrder || !actionMachine) return;
        // Abmelden-Modal öffnen (mit geklicktem Tag vorbelegen)
        openFulfillModalFor(actionOrder, actionMachine, actionDateStr || null);
        setShowActionModal(false);
      }}
      style={{
  display:'block',
  width:'100%',
  padding:'10px 14px',
  borderRadius:10,
  border:'1px solid #ddd',
  background:'#fff',
  textAlign:'center',
  cursor:'pointer',
  // ⬇️ fix gegen “unsichtbare” Labels
  color:'#212529',
  fontSize:14,
  fontWeight:500,
  lineHeight:1.2,
  whiteSpace:'normal',
  textIndent: 0
}}

    >
      Maschine abmelden
    </button>
  </div>

  <div style={{ marginTop: 14, display:'flex', gap:8, justifyContent:'flex-end' }}>
    <button
      onClick={() => setShowActionModal(false)}
      style={{ background:'#eee', color:'#333', padding:'8px 16px', border:'none', borderRadius:5 }}
    >
      Abbrechen
    </button>
    <button
      onClick={() => {
        if (actionOrder) navigate(`/orders/${actionOrder.id}/edit`);
      }}
      style={{ background:'#007bff', color:'#fff', padding:'8px 16px', border:'none', borderRadius:5 }}
    >
      Auftrag bearbeiten
    </button>
  </div>
</SimpleModal>

<SimpleModal
  open={showOfferActionModal}
  onClose={() => {
    setShowOfferActionModal(false);
    setActionOffer(null);
    setActionOfferItem(null);
    setActionOfferMachine(null);
    setActionOfferDateStr(null);
  }}
>
  <h3>Was möchtest du tun?</h3>
  <p style={{ marginTop: 4, marginBottom: 14 }}>
    <strong>Angebot:</strong> {actionOffer?.offerNumber || actionOffer?.id || '—'}<br/>
    <strong>Maschine:</strong> {actionOfferMachine?.name || '—'}
  </p>

  <div style={{ display:'grid', gap:8, marginTop:6 }}>
    <div style={{ fontWeight:600, textAlign:'left' }}>Druckansicht</div>
    <button
      type="button"
      onClick={() => {
        if (actionOffer?.id) openCalendarPdf(`offer/${actionOffer.id}/pdf`);
      }}
      style={{
        display:'block', width:'100%', padding:'10px 14px',
        borderRadius:8, border:'none', background:'#28a745',
        textAlign:'center', cursor:'pointer', color:'#fff', fontSize:14,
        marginBottom:4
      }}
    >
      Angebot als PDF
    </button>

    {/* Maschine ab Datum tauschen (Angebot) */}
    <button
      onClick={() => {
        if (!actionOffer || !actionOfferItem) return;
        setSwapOffer(actionOffer);
        setSwapOfferItem(actionOfferItem);
        const segFrom = actionOfferItem?.rentalPeriodFrom
          ? moment(actionOfferItem.rentalPeriodFrom).format('YYYY-MM-DD')
          : moment().add(1, 'day').format('YYYY-MM-DD');
        // Start bevorzugt der geklickte Tag (falls im Segment), sonst Segment-Start
        const d = actionOfferDateStr ? moment(actionOfferDateStr) : null;
        const inSeg = d &&
          moment(segFrom).isSameOrBefore(d, 'day') &&
          moment(actionOfferItem.rentalPeriodTo).isSameOrAfter(d, 'day');
        setSwapOfferStartDate(inSeg ? d.format('YYYY-MM-DD') : segFrom);
        setSwapOfferNewItem(null);
        setShowOfferActionModal(false);
        setShowSwapOfferModal(true);
      }}
      style={{
        display:'block', width:'100%', padding:'10px 14px',
        borderRadius:10, border:'1px solid #ddd', background:'#fff',
        textAlign:'center', cursor:'pointer', color:'#212529', fontSize:14, fontWeight:500
      }}
    >
      Maschine ab Datum tauschen (Angebot)
    </button>

    {/* Mietdauer (Angebot) anpassen */}
    <button
      onClick={() => {
        if (!actionOffer || !actionOfferItem) return;
        setRangeOffer(actionOffer);
        setRangeOfferItem(actionOfferItem);
        setRangeOfferFrom(moment(actionOfferItem.rentalPeriodFrom).format('YYYY-MM-DD'));
        setRangeOfferTo(moment(actionOfferItem.rentalPeriodTo).format('YYYY-MM-DD'));
        setShowOfferActionModal(false);
        setShowOfferRangeModal(true);
      }}
      style={{
        display:'block', width:'100%', padding:'10px 14px',
        borderRadius:10, border:'1px solid #ddd', background:'#fff',
        textAlign:'center', cursor:'pointer', color:'#212529', fontSize:14, fontWeight:500
      }}
    >
      Mietdauer (Angebot) anpassen
    </button>
  </div>

  <div style={{ marginTop: 14, display:'flex', gap:8, justifyContent:'flex-end' }}>
    <button
      onClick={() => setShowOfferActionModal(false)}
      style={{ background:'#eee', color:'#333', padding:'8px 16px', border:'none', borderRadius:5 }}
    >
      Abbrechen
    </button>
    <button
      onClick={() => { if (actionOffer) navigate(`/offers/${actionOffer.id}/edit`); }}
      style={{ background:'#007bff', color:'#fff', padding:'8px 16px', border:'none', borderRadius:5 }}
    >
      Angebot bearbeiten
    </button>
  </div>
</SimpleModal>

<SimpleModal
  open={showLocationModal}
  onClose={() => {
    setShowLocationModal(false);
    setLocationInfo(null);
  }}
>
  <h3>Telematik-Standort</h3>

  <p style={{ marginBottom: 8 }}>
    <strong>Maschine:</strong> {locationInfo?.machineName || '—'}
  </p>

  <p style={{ marginBottom: 4 }}>
    <strong>Letzter Positionsstand:</strong>{' '}
    {locationInfo?.timestamp
      ? moment(locationInfo.timestamp).format('DD.MM.YYYY HH:mm')
      : 'keine Zeitangabe vorhanden'}
  </p>

  <p style={{ fontSize: 12, opacity: 0.75 }}>
    Koordinaten: {locationInfo?.lat ?? '—'}, {locationInfo?.lng ?? '—'}
  </p>

  <div
    style={{
      marginTop: 14,
      display: 'flex',
      gap: 8,
      justifyContent: 'flex-end',
    }}
  >
    <button
      onClick={() => {
        setShowLocationModal(false);
        setLocationInfo(null);
      }}
      style={{
        background: '#eee',
        color: '#333',
        padding: '8px 16px',
        border: 'none',
        borderRadius: 5,
      }}
    >
      Abbrechen
    </button>

    <button
      onClick={handleConfirmOpenMaps}
      style={{
        background: '#2eae4a',
        color: '#fff',
        padding: '8px 16px',
        border: 'none',
        borderRadius: 5,
      }}
    >
      In Google Maps öffnen
    </button>
  </div>
</SimpleModal>


    {/* Flex-Container für die Dropdown-Menüs */}
<div
  style={{
    display: 'flex',
    alignItems: 'flex-start',
    gap: '20px',
    marginBottom: '0px',
    position: 'relative',
    minHeight: 64 // Höhe der oberen Reihe; untere Inputs sind absolut positioniert
  }}
>
  {/* Monat + Maschine (darunter, absolut) */}
  <div style={{ textAlign: 'left', width: 150, position: 'relative' }}>
    <label
      htmlFor="month-select"
      style={{ display: 'block', margin: '0 0 0 15px' }}
    >
      Monat
    </label>
    <select
      id="month-select"
      value={month}
      onChange={handleMonthChange}
      style={{
        width: '150px',
        padding: '5px',
        border: '1px solid #ccc',
        borderRadius: '4px'
      }}
    >
      {Array.from({ length: 12 }, (_, index) => (
        <option key={index} value={index}>
          {moment().month(index).format('MMMM')}
        </option>
      ))}
    </select>

    {/* Maschine direkt unter Monat, ohne Leistenhöhe zu vergrößern */}
    <input
      id="machine-filter"
      type="text"
      list="machine-options"
      value={machineFilter}
      onChange={handleMachineFilterChange}
      placeholder="Maschine"
      style={{
        position: 'absolute',
        left: 14,
        top: 75, // ggf. feinjustieren (52–60), damit direkt unter dem Select
        width: 150,
        padding: 5,
        border: '1px solid #ccc',
        borderRadius: 4,
        background: '#fff',
        zIndex: 2
      }}
    />
    <datalist id="machine-options">
      {uniqueMachineNames.map((name) => (
        <option key={name} value={name} />
      ))}
    </datalist>
  </div>

  {/* Jahr + Auftragsnummer (darunter, absolut) */}
  <div style={{ textAlign: 'left', width: 150, position: 'relative' }}>
    <label
      htmlFor="year-select"
      style={{ display: 'block', margin: '0 0 0 15px' }}
    >
      Jahr
    </label>
    <select
      id="year-select"
      value={year}
      onChange={handleYearChange}
      style={{
        width: '150px',
        padding: '5px',
        border: '1px solid #ccc',
        borderRadius: '4px'
      }}
    >
      {generateYearOptions().map((yr) => (
        <option key={yr} value={yr}>
          {yr}
        </option>
      ))}
    </select>

    {/* Auftragsnummer direkt unter Jahr */}
    <input
      id="order-filter"
      type="text"
      list="order-options"
      value={orderFilter}
      onChange={handleOrderFilterChange}
      placeholder="Auftragsnummer"
      style={{
        position: 'absolute',
        left: 14,
        top: 75, // ggf. feinjustieren
        width: 150,
        padding: 5,
        border: '1px solid #ccc',
        borderRadius: 4,
        background: '#fff',
        zIndex: 2
      }}
    />
    <datalist id="order-options">
      {orderOptions.map((num) => (
        <option key={num} value={num} />
      ))}
    </datalist>
  </div>

  {/* Produktgruppe */}
  <div style={{ textAlign: 'left' , width: 200, position: 'relative',}}>
    <label
      htmlFor="productGroup-select"
      style={{ display: 'block', margin: '0 0 0 15px' }}
    >
      Produktgruppe
    </label>
    <select
      id="productGroup-select"
      value={selectedProductGroup}
      onChange={handleProductGroupChange}
      style={{
        width: '200px',
        padding: '5px',
        border: '1px solid #ccc',
        borderRadius: '4px'
      }}
    >
      <option value="all">Alle</option>
      {productGroups.map((group) => (
        <option key={group.id} value={group.id}>
          {group.name}
        </option>
      ))}
    </select>
  </div>

  {/* Lagerort */}
  <div style={{ textAlign: 'left' , width: 180, position: 'relative',}}>
    <label
      htmlFor="storageLocation-select"
      style={{ display: 'block', margin: '0 0 0 15px' }}
    >
      Lagerort
    </label>
    <select
      id="storageLocation-select"
      value={selectedStorageLocation}
      onChange={handleStorageLocationChange}
      style={{
        width: '180px',
        padding: '5px',
        border: '1px solid #ccc',
        borderRadius: '4px'
      }}
    >
      <option value="all">Alle</option>
      {storageLocations.map((location) => (
        <option key={location.id} value={location.id}>
          {location.name}{location.isActive === false ? ' (inaktiv)' : ''}
        </option>
      ))}
    </select>
  </div>

  {/* Motorisierung */}
  <div style={{ textAlign: 'left', width: 150, position: 'relative', }}>
    <label
      htmlFor="motorization-select"
      style={{ display: 'block', margin: '0 0 0 15px' }}
    >
      Motorisierung
    </label>
    <select
      id="motorization-select"
      value={selectedMotorization}
      onChange={handleMotorizationChange}
      style={{
        width: '150px',
        padding: '5px',
        border: '1px solid #ccc',
        borderRadius: '4px'
      }}
    >
      <option value="all">Alle</option>
      {motorTypes.map((motor) => (
        <option key={motor.id} value={motor.id}>
          {motor.name}
        </option>
      ))}
    </select>
  </div>
 {/* NEU: Arbeitshöhe-Filter (Mindesthöhe) */}
  {heightStats.max > 0 && (
    <div style={{ textAlign: 'left', width: 260 }}>
      <label
        htmlFor="height-range"
        style={{ display: 'block', margin: '0 0 0 15px' }}
      >
        Arbeitshöhe (min.)
      </label>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '4px 12px 0 12px'
        }}
      >
        <input
          id="height-range"
          type="range"
          min={heightStats.min}
          max={heightStats.max}
          step={1}
          value={
            minHeightFilter === ''
              ? heightStats.min
              : Number(minHeightFilter)
          }
          onChange={(e) => setMinHeightFilter(e.target.value)}
          style={{ flex: 1 }}
        />
        <div style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
          ab{' '}
          {formatHeight(
            minHeightFilter === ''
              ? heightStats.min
              : Number(minHeightFilter)
          )}
        </div>
        <button
          type="button"
          onClick={() => setMinHeightFilter('')}
          title="Höhenfilter zurücksetzen"
          style={{
            border: 'none',
            background: '#f1f3f5',
            borderRadius: 4,
            padding: '2px 6px',
            cursor: 'pointer'
          }}
        >
          ✕
        </button>
      </div>
    </div>
  )}


</div>

      {/* Zeitraum-Navigation */}
<div
  className="range-nav"
  style={{
    textAlign: 'center',
    marginBottom: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8
  }}
>
  <button
    type="button"
    className="range-btn"
    onClick={() => shiftRange(-14)}
    aria-label="2 Wochen zurück"
    title="2 Wochen zurück"
    style={{ background: '#EBEBEB', color: '#000000ff' }} 
  >
    ‹
  </button>

  <p style={{ margin: 0 }}>
    Anzeigen von {moment(startDate).format('DD.MM.YYYY')} bis {moment(endDate).format('DD.MM.YYYY')}
  </p>

  <button
    type="button"
    className="range-btn"
    onClick={() => shiftRange(14)}
    aria-label="2 Wochen vor"
    title="2 Wochen vor"
    style={{ background: '#EBEBEB', color: '#000000ff' }} 
  >
    ›
  </button>

  <button
  type="button"
  className="range-btn"
  onClick={goToday}
  title="Heute (± 3 Wochen)"
  style={{ background: '#EBEBEB', color: '#000' }}
>
  Heute
</button>

</div>


      {/* Kalender anzeigen */}
<div
  className="table-scroll-container"
  ref={rowsParentRef}
  style={{
    maxHeight: calendarScrollMaxHeight ? `${calendarScrollMaxHeight}px` : 'calc(100vh - 220px)',
    overflowY: 'auto',
    overflowX: 'auto',
    position: 'relative'
  }}
>
<div
    className="calendar-table-wrapper"
    ref={wrapperRef}
    style={{ position: 'relative' }}
  >
    {calendarAutoRefreshEnabled && (
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '0 25px 4px 0',
          fontSize: 12,
          color: '#555',
          whiteSpace: 'nowrap'
        }}
      >
        {isLoading
          ? 'Aktualisiere ...'
          : lastCalendarRefreshAt
            ? `Zuletzt aktualisiert: ${moment(lastCalendarRefreshAt).format('HH:mm:ss')}`
            : 'Noch nicht aktualisiert'}
      </div>
    )}

    {todayOverlay.show && (
      <div
        className="today-column-overlay"
        style={{ left: todayOverlay.left, width: todayOverlay.width, pointerEvents: 'none' }}
      />
    )}

    <table className="calendar-table">
      <thead>
    <tr>
      <th>Maschine</th>
      {generateDateRange.map((date) => {
        const dayOfWeek = date.day(); // 0=So, 6=Sa
        const isWeekend = [0, 6].includes(dayOfWeek);
        const holiday = isHolidayDate(date.toDate());
        const isToday = date.isSame(moment(), 'day');

        // NEU: ISO-Kalenderwoche + Montag-Check
        const isoWeek = date.isoWeek();           // z. B. 42
        const isMonday = date.isoWeekday() === 1; // 1 = Montag (ISO)

        return (
          <th
            key={date.toISOString()}
            ref={isToday ? todayThRef : null}      // ⬅️ wichtig für die Messung
            className={`${holiday ? 'holiday-header' : ''} ${isWeekend ? 'weekend-header' : ''} ${isToday ? 'today-header today-col' : ''}`}
          >
            <div className="day-header">
              
              <div>
                <span className="day-num">{date.format('D')}</span>
                <span className="weekday-abbr">{date.format('dd')}</span>
              </div>

              {/* KW nur am Montag anzeigen */}
              {isMonday && (
                <div className="week-label">
                  <strong>{isoWeek}</strong>
                </div>
              )}


            </div>
          </th>
        );
      })}
    </tr>
  </thead>
      <tbody>
  {useVirtual ? (
    (() => {
      const vItems = rowVirtualizer.getVirtualItems();
      if (!vItems.length) return null;

      const startIndex = vItems[0].index;
      const endIndex   = vItems[vItems.length - 1].index;

      const visibleRows = filteredRentalData.slice(startIndex, endIndex + 1);

      const topSpacerHeight =
        vItems[0].start || 0;

      const bottomSpacerHeight =
        rowVirtualizer.getTotalSize() - (vItems[vItems.length - 1].end || 0);

      return (
        <>
          {topSpacerHeight > 0 && (
            <tr className="calendar-virtual-spacer" aria-hidden="true">
              <td colSpan={generateDateRange.length + 1} style={{ height: topSpacerHeight }} />
            </tr>
          )}
          {generateItemRows(visibleRows)}
          {bottomSpacerHeight > 0 && (
            <tr className="calendar-virtual-spacer" aria-hidden="true">
              <td colSpan={generateDateRange.length + 1} style={{ height: bottomSpacerHeight }} />
            </tr>
          )}
        </>
      );
    })()
  ) : (
    // Klassisch: alle Zeilen rendern (kein Virtualizer nötig)
    generateItemRows()
  )}
</tbody>


    </table>
  </div>
</div>
</div>
  );
};

export default RentalCalendar;
